/**
 * Application - Main Application Controller
 * Replaces GLOBAL.java singleton from Java version
 *
 * Phase 1: Core Infrastructure Demo
 * - Three.js rendering
 * - Rapier.js physics
 * - Simple cube with physics simulation
 */

import * as THREE from 'three';
import { SceneManager } from '../rendering/SceneManager.js';
import { PhysicsWorld } from '../physics/PhysicsWorld.js';
import { Ragdoll } from '../physics/Ragdoll.js';
import { SketchPlane } from '../sketch/SketchPlane.js';
import { MeshBuilder } from '../geometry/MeshBuilder.js';
import { unionShapes, sampleRing, subtractCutouts } from '../geometry/PolygonOps.js';
import { SlicePlaneManager } from '../geometry/SlicePlaneManager.js';
import { SlicePlane } from '../geometry/SlicePlane.js';
import { CrossSliceSelection } from '../geometry/CrossSliceSelection.js';
import { SliceType } from '../geometry/SliceType.js';
import { SketchSpline } from '../sketch/SketchSpline.js';
import { CameraController } from '../rendering/CameraController.js';
import { PlaneIntersector, composeFrameMatrix } from '../interaction/PlaneIntersector.js';
import { SketchRenderer3D } from '../sketch/SketchRenderer3D.js';
import { ControlPointRenderer } from '../rendering/ControlPointRenderer.js';
import { SceneRotationController, VIEW_PRESETS } from '../rendering/SceneRotationController.js';
import { UIManager } from '../ui/UIManager.js';
import { CloudUI } from '../ui/cloud/CloudUI.js';
import { UndoManager } from './UndoManager.js';
import { DesignIO } from './DesignIO.js';
import { StrokeStore, generateStrokeId } from './StrokeStore.js';
import { LayerManager } from './LayerManager.js';
import { DrawingPreview } from './DrawingPreview.js';
import { SceneVisualizers } from '../rendering/SceneVisualizers.js';
import { ChairPhysics } from '../physics/ChairPhysics.js';
import { PhysicsDebugRenderer } from '../physics/PhysicsDebugRenderer.js';

export class Application {
    constructor() {
        // PHASE 3.5: Unified 3D View feature flag
        // Set to true to enable unified view (orthographic camera, 2D-in-3D drawing)
        // Set to false for legacy split-pane mode (perspective camera)
        this.USE_UNIFIED_VIEW = true;

        // Get 3D canvas element
        this.canvas = document.getElementById('render-canvas');
        if (!this.canvas) {
            throw new Error('3D Canvas element not found');
        }

        // Get 2D sketch canvas (only used in legacy mode)
        this.sketchCanvas = document.getElementById('sketch-canvas');
        if (!this.sketchCanvas && !this.USE_UNIFIED_VIEW) {
            throw new Error('Sketch canvas element not found');
        }

        // Phase 3.5: Camera controller and sketch renderer for unified view
        if (this.USE_UNIFIED_VIEW) {
            const width = this.canvas.clientWidth;
            const height = this.canvas.clientHeight;
            this.cameraController = new CameraController(width, height);
            this.planeIntersector = new PlaneIntersector();
            this.sketchRenderer3D = new SketchRenderer3D('canvas'); // Use canvas texture mode
            this.controlPointRenderer = new ControlPointRenderer(); // Control point overlays
            this.sceneRotation = new SceneRotationController(); // Scene rotation (not camera)
            this.currentDrawingPreview = null; // Real-time drawing preview line

            // Shared frame for ALL 2D sketch overlays (stroke outlines,
            // control points, drawing/slice previews). Child of the rotation
            // pivot; posed each frame with the chair's physics frame delta so
            // the drawing plane follows the simulated chair (Java parity).
            this.overlayFrame = new THREE.Group();
            this.overlayFrame.name = 'SketchOverlayFrame';
            this.sceneRotation.getPivotGroup().add(this.overlayFrame);
        }

        // Core systems
        const sceneOptions = this.USE_UNIFIED_VIEW ? {
            cameraMode: 'orthographic',
            cameraController: this.cameraController
        } : {};

        this.sceneManager = new SceneManager(this.canvas, sceneOptions);
        this.physicsWorld = new PhysicsWorld();
        this.ragdoll = null; // Physics ragdoll (ergonomic figure)
        this.isPhysicsPlaying = false; // Physics simulation state
        this.chairPhysicsBodies = []; // Physics bodies for chair meshes
        this.chairPhysicsMeshes = []; // Corresponding meshes for cleanup
        this.savedMeshTransforms = []; // Saved positions/rotations for physics reset
        this.savedCompoundBodyTransform = null; // Saved compound body position/rotation for physics reset
        this.physicsFrameDelta = null; // {position, quaternion} chair pose delta while simulating (null = identity)
        this.physicsOverlayActive = false; // True from physics play until reset/restore

        // Phase 3.5: Create SketchPlane in appropriate mode
        if (this.USE_UNIFIED_VIEW) {
            this.sketchPlane = new SketchPlane(null, { mode: '3d' });
        } else {
            this.sketchPlane = new SketchPlane(this.sketchCanvas, { mode: '2d' });
        }

        this.meshBuilder = new MeshBuilder();
        this.slicePlaneManager = new SlicePlaneManager(this.sceneManager); // Pass scene manager for debug visualization

        // Extracted subsystems (delegation pattern - see one-line delegate
        // methods below that forward to these managers)
        this.undoManager = new UndoManager(this);
        this.designIO = new DesignIO(this);
        this.layerManager = new LayerManager(this);
        this.drawingPreview = new DrawingPreview(this);
        this.sceneVisualizers = new SceneVisualizers(this);
        this.chairPhysics = new ChairPhysics(this);
        this.physicsDebug = new PhysicsDebugRenderer(this); // Collider hull wireframes ('h' key / PhysicsPanel)

        // BASE PLANES SYSTEM (the "layers")
        // These are parallel planes that contain the user's sketches
        this.basePlanes = []; // Array of SlicePlane objects
        this.basePlaneSketchStates = []; // RESOLVED sketch state for each base plane (see strokeStore)

        // LINKED MASTER STROKES: document-level stroke masters + per-layer
        // instance tables. basePlaneSketchStates above is now a RESOLVED
        // cache materialized from this store on every SYNC (see
        // syncStrokeStore/resolveLayerStates) - downstream consumers keep
        // reading plain per-layer states, unchanged.
        this.strokeStore = new StrokeStore(0);
        // The layer the live editable sketch (sketchPlane) currently
        // mirrors - the reference layer for stroke diffing in SYNC
        this.activeSketchLayerIndex = 0;
        this.selectedBasePlaneIndices = new Set(); // Set of selected plane indices (can be multiple)
        this.chairWidth = 300; // Total chair width in mm (controlled by slider)
        this.numBasePlanes = 5; // Number of base planes to create
        this.basePlaneSpacing = this.chairWidth / (this.numBasePlanes - 1); // Calculated spacing between base planes
        this.mirrorMode = false; // Mirror mode: select/unselect mirrored pairs simultaneously

        // FABRICATION SETTINGS (Fabrication tab)
        // materialThickness is the DESIGN value (edited in the Layers
        // section). fabSettings.thicknessOverride, when set (Make panel),
        // takes precedence for the EFFECTIVE thickness used by planes,
        // slots and exports - see getEffectiveThickness().
        this.materialThickness = 5.0; // Material thickness in mm (design value)
        // Standard US sheet material: 4x8 ft (48"x96") = 1219x2438 mm.
        // cutScalePct scales the cut pattern (100% = real size); presets set
        // it so small laser beds / paper shrink the model to fit.
        this.fabSettings = { sheetWidth: 1219, sheetHeight: 2438, gap: 10, cutScalePct: 100, thicknessOverride: null };

        // Application state
        this.isRunning = false;
        this.lastTime = 0;
        this.fps = 0;
        this.frameCount = 0;
        this.fpsUpdateTime = 0;

        // Demo objects
        this.cubes = [];
        this.generatedMeshes = []; // Meshes generated from 2D sketches
        this.planeVisualizations = []; // Visual representations of slice planes
        this.intersectionLines = []; // Visual representations of intersections
        this.generatedPlanes = []; // Slice planes generated from splines
        this.crossSliceSelections = []; // CrossSliceSelection objects for each spline
        this.slotVisualizations = []; // Visual representations of slots

        // Demo mode (toggle with 'P' key)
        this.showPlaneDemo = false;

        // Clipboard for copy/paste between planes
        this.clipboard = null;

        // Interactive slice placement (armed by the Slices panel buttons).
        // When set, drawing pointerdown places slices instead of sketching.
        // {type: 'single'|'group', pending: {shapeIndex, t}|null}
        this.sliceTool = null;

        // Active click-drag edit of an existing slice group's rib
        // {selection, mode: 'single'|'start'|'end'|'whole', grabT, origStart, origEnd, lastRegen}
        this.sliceDrag = null;

        // HAND TOOL: active physics grab of the chair's compound body
        // {body, localOffset(V3), plane(THREE.Plane, world), targetLocal(V3)}
        this.handGrab = null;

        // Ergonomic-figure selection: a click (not a drag) on the figure
        // selects it, floating a control pill above its head (height/age
        // readout + sit/stand + size). Lazily built in ensureFigureWidget().
        this._figureSelected = false;
        this._figureWidget = null;

        // Where the user has parked the chair with the hand tool (offset from
        // the generated pose, pivot-local). Regeneration rebuilds the compound
        // body at the fresh centre of mass, so this is re-applied afterwards -
        // otherwise editing a point would snap a dragged chair back.
        this.chairMoveOffset = new THREE.Vector3();

        // MEASURE TOOL: current measurement {x0,y0,x1,y1,dragging} (sketch px = mm)
        this.measurement = null;

        // SCALE TOOL: in-progress uniform scale of the whole design
        // {cx,cy, startDist, snapshot, startChairWidth, activeLayer, ...}
        this.scaleGesture = null;

        // Settings
        this.planeSpacing = 50; // Spacing between parallel planes in pixels
    }

    /**
     * Initialize application
     */
    async init() {
        console.log('Initializing SketchChair.js application...');

        // Initialize 3D rendering
        this.sceneManager.init();

        // Phase 3.5: Setup scene rotation pivot (unified view)
        if (this.USE_UNIFIED_VIEW && this.sceneRotation) {
            this.sceneManager.setRotationPivot(this.sceneRotation.getPivotGroup());
        }

        // Initialize physics (async WASM load)
        await this.physicsWorld.init();

        // Create physics ragdoll (ergonomic figure for chair testing)
        this.ragdoll = new Ragdoll(this.physicsWorld, this.sceneManager);
        await this.ragdoll.init();
        this.ragdoll.setVisible(false); // Start hidden by default
        console.log('Physics ragdoll initialized (hidden by default - press "d" to toggle)');

        // Create ground plane visualization
        this.createGroundPlane();

        // Initialize 2D sketch canvas (only in legacy mode)
        if (!this.USE_UNIFIED_VIEW && this.sketchCanvas) {
            this.initSketchCanvas();
        }

        // Setup shape change callback (for both 2D and 3D modes)
        if (this.USE_UNIFIED_VIEW) {
            this.setupSketchCallbacks();
        }

        // Initialize base planes (the layer system)
        this.initializeBasePlanes();

        // Create demo scene - DISABLED (no longer needed, ragdoll is main physics test)
        // this.createDemoScene();

        // Setup keyboard controls
        this.setupKeyboardControls();

        // Setup camera controls (Phase 3.5 unified view)
        if (this.USE_UNIFIED_VIEW) {
            this.setupCameraControls();
            this.setupDrawingInteraction();
        }

        // Initialize UI (Phase 6)
        this.uiManager = new UIManager(this);
        console.log('UI initialized');

        // Cloud layer (Phase 7): File menu, Publish, accounts. The app
        // works fully offline without it, so a failure here must never
        // take the editor down.
        try {
            this.cloudUI = new CloudUI(this);
        } catch (e) {
            console.error('Cloud UI failed to initialize (offline mode):', e);
            this.cloudUI = null;
        }

        console.log('Application initialization complete');
        console.log('All base planes selected - drawing on all planes simultaneously');
        console.log('Press "[" and "]" to navigate between individual planes');
        console.log('Press "A" to select all planes');
        console.log('Press "{" and "}" to add/remove base planes');
        console.log("Press ',' to open scene settings (lights, shadows, background)");
        console.log("Press 'h' for the hand tool (drag the chair: spring during physics, free XY move while paused); Shift+H toggles hull debug");
    }

    /**
     * Create ground plane visualization
     * Adds a large gray plane and thin box at Y=0 to show ground level
     * Includes vertical walls on the sides for visibility from any angle
     */
    createGroundPlane() {
        const groundY = -5; // Ground level (below origin)

        // Dot-grid "paper" ground: one huge plane with a tiny repeating
        // canvas texture - effectively infinite at any zoom, one draw call.
        const DOT_SPACING_WORLD = 0.25;   // 25mm between dots
        const TILE = 64;                  // px per tile
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = TILE;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#f4f4f4';
        ctx.fillRect(0, 0, TILE, TILE);
        ctx.fillStyle = 'rgba(110, 118, 128, 0.8)';
        ctx.beginPath();
        ctx.arc(TILE / 2, TILE / 2, 5, 0, Math.PI * 2);
        ctx.fill();

        const texture = new THREE.CanvasTexture(canvas);
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        // No mipmaps: at thousands of repeats the tiny dots average away to
        // nothing in the mip chain and the grid vanishes at any distance
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        const SIZE = 2000; // world units - far beyond any usable zoom
        texture.repeat.set(SIZE / DOT_SPACING_WORLD, SIZE / DOT_SPACING_WORLD);
        texture.anisotropy = this.sceneManager.renderer?.capabilities?.getMaxAnisotropy?.() ?? 1;

        // OPAQUE dot plane: transparent materials land in the transparent
        // render pass where the big translucent base-plane visuals paint
        // over them (why the first attempt was invisible). Opaque + baked
        // light background renders in the opaque pass like the old box did.
        const dotPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(SIZE, SIZE),
            new THREE.MeshBasicMaterial({ map: texture, depthWrite: false })
        );
        dotPlane.rotation.x = -Math.PI / 2;
        dotPlane.position.y = groundY;
        dotPlane.renderOrder = -1; // backdrop: never occludes the chair
        this.sceneManager.add(dotPlane);

        // Centre cross at ground level: reads as the origin marker from
        // above and as a thin grey ground line when viewed side-on
        const lineMat = new THREE.LineBasicMaterial({ color: 0xb9bfc6 });
        const crossX = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-SIZE / 2, groundY + 0.002, 0),
            new THREE.Vector3(SIZE / 2, groundY + 0.002, 0)
        ]), lineMat);
        const crossZ = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, groundY + 0.002, -SIZE / 2),
            new THREE.Vector3(0, groundY + 0.002, SIZE / 2)
        ]), lineMat);
        crossX.renderOrder = -1;
        crossZ.renderOrder = -1;
        this.sceneManager.add(crossX);
        this.sceneManager.add(crossZ);

        // Shadow catcher on top of the dots (transparent, shadows only)
        const shadowPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(SIZE, SIZE),
            new THREE.ShadowMaterial({ opacity: 0.18 })
        );
        shadowPlane.rotation.x = -Math.PI / 2;
        shadowPlane.position.y = groundY + 0.001;
        shadowPlane.receiveShadow = true;
        shadowPlane.renderOrder = -1;
        this.sceneManager.add(shadowPlane);

        // Track floor visuals for the floor toggle ('f' key, matches Java)
        this.groundVisuals = [dotPlane, shadowPlane, crossX, crossZ];

        console.log('Dot-grid ground created at Y=' + groundY);
    }

    /**
     * Initialize base planes (the "layers" that contain sketches)
     * Creates parallel planes at different Z positions
     */
    initializeBasePlanes() { return this.layerManager.initializeBasePlanes(); }

    /**
     * Visualize base planes in the 3D scene
     */
    visualizeBasePlanes() { return this.layerManager.visualizeBasePlanes(); }

    /**
     * Select a single base plane (deselects all others)
     * @param {number} index - Index of plane to select
     */
    selectBasePlane(index) { return this.layerManager.selectBasePlane(index); }

    /**
     * Select all base planes
     */
    selectAllPlanes() { return this.layerManager.selectAllPlanes(); }

    /**
     * Set chair width and update base planes
     * @param {number} width - Chair width in mm
     */
    async setChairWidth(width) {
        this.chairWidth = width;
        this.basePlaneSpacing = this.chairWidth / (this.numBasePlanes - 1);
        console.log(`Chair width set to ${width}mm, spacing: ${this.basePlaneSpacing.toFixed(2)}mm`);

        // Update existing base plane positions instead of recreating them
        this.updateBasePlanePositions();

        // One canonical regeneration at the new width (slices, slots, leg
        // braces, meshes, physics). AWAITED so callers of setChairWidth
        // get the settled state: the compound body is cleared + recreated
        // at the tail of this regeneration.
        await this.regenerateAll();

        // Update status display
        this.updateStatusDisplay();
    }

    /**
     * Update base plane positions without recreating them
     * Maintains sketch states and just repositions planes
     */
    updateBasePlanePositions() { return this.layerManager.updateBasePlanePositions(); }

    /**
     * LEGACY wrapper: partial cross-slice rebuilds are gone - everything
     * routes through the canonical pipeline (see regenerateAll) so leg
     * braces, slots, meshes and physics can never go stale independently.
     */
    async updateCrossSlicePositions() {
        return this.regenerateAll();
    }

    /**
     * LEGACY wrapper - see updateCrossSlicePositions/regenerateAll
     */
    async regenerateCrossSlices() {
        return this.regenerateAll();
    }

    /**
     * Add a new layer by copying an adjacent layer
     * Inserts the new layer next to the currently selected layer
     */
    addNewLayer() { return this.layerManager.addNewLayer(); }

    /**
     * Toggle mirror mode on/off
     */
    toggleMirrorMode() { return this.layerManager.toggleMirrorMode(); }

    /**
     * Toggle guide layer status for currently selected layers
     */
    toggleGuideLayer() { return this.layerManager.toggleGuideLayer(); }

    /**
     * Get the mirrored index for a given layer index
     * @param {number} index - Layer index
     * @returns {number} Mirrored layer index
     */
    getMirroredIndex(index) { return this.layerManager.getMirroredIndex(index); }

    /**
     * Select a base plane with mirror mode support
     * If mirror mode is on, also selects/unselects the mirrored layer
     * @param {number} index - Layer index to select
     * @param {boolean} toggle - If true, toggle selection instead of replacing
     */
    selectBasePlaneWithMirror(index, toggle = false) { return this.layerManager.selectBasePlaneWithMirror(index, toggle); }

    /**
     * Clear plane visualizations
     */
    clearPlaneVisualizations() { return this.layerManager.clearPlaneVisualizations(); }

    /**
     * Setup keyboard controls
     */
    setupKeyboardControls() {
        window.addEventListener('keydown', (e) => {
            // Don't trigger shortcuts while typing in a form field
            const target = e.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }

            const setTool = (toolId) => {
                if (this.uiManager?.panels?.tools) {
                    this.uiManager.panels.tools.setTool(toolId);
                } else if (this.sketchPlane) {
                    this.sketchPlane.setTool(toolId);
                }
            };

            // Tool shortcuts (match the Java version: SketchTools.keyPressed)
            if (!e.ctrlKey && !e.metaKey && !e.altKey) {
                if (e.key === 'd' || e.key === 'D') setTool('brush');   // draw tool
                if (e.key === 'a' || e.key === 'A') setTool('select');  // select tool
                if (e.key === 'b' || e.key === 'B') setTool('bezier');  // bezier select
                if (e.key === 'o' || e.key === 'O') setTool('offset');  // offset path
                if (e.key === 'p' || e.key === 'P') setTool('path');    // draw path
                if (e.key === 'l' || e.key === 'L') setTool('leg');     // leg tool
                if ((e.key === 'h' || e.key === 'H') && !e.shiftKey) setTool('hand'); // hand (physics grab)

                // Floor toggle (matches Java's 'f')
                if (e.key === 'f' || e.key === 'F') {
                    this.toggleFloor();
                }

                // Physics collider hull debug draw (Shift+H - plain 'h'
                // now selects the hand tool)
                if (e.key === 'H' && e.shiftKey) {
                    this.togglePhysicsDebug();
                }

                // Scene settings card (editor convention; Escape closes)
                if (e.key === ',') {
                    this.uiManager?.panels?.sceneSettings?.toggle();
                    e.preventDefault();
                }

                // Leg-brace debug overlay (seat anchor / crossings / rects)
                if (e.key === 'j' || e.key === 'J') {
                    this.toggleLegBraceDebug();
                }

                // Space: toggle isometric <-> side view (same presets as the
                // Views buttons in the camera panel)
                if (e.key === ' ' && this.sceneRotation) {
                    const { x, y } = this.sceneRotation.getRotation();
                    const atSide = Math.abs(x - VIEW_PRESETS.side.x) < 0.01 &&
                        Math.abs(y - VIEW_PRESETS.side.y) < 0.01;
                    const next = atSide ? VIEW_PRESETS.iso : VIEW_PRESETS.side;
                    this.sceneRotation.setRotation(next.x, next.y);
                    e.preventDefault();
                }

                // Delete/Backspace: delete the selected shape
                if (e.key === 'Delete' || e.key === 'Backspace') {
                    if (this.sketchPlane.deleteSelected()) {
                        e.preventDefault();
                    }
                }
            }

            // Navigate between base planes
            if (e.key === '[') {
                // Previous plane
                if (this.basePlanes.length > 0) {
                    // Get the current single selected plane, or start from first
                    let currentIndex = 0;
                    if (this.selectedBasePlaneIndices.size === 1) {
                        currentIndex = Array.from(this.selectedBasePlaneIndices)[0];
                    }
                    const newIndex = (currentIndex - 1 + this.basePlanes.length) % this.basePlanes.length;
                    this.selectBasePlane(newIndex);
                }
            }

            if (e.key === ']') {
                // Next plane
                if (this.basePlanes.length > 0) {
                    // Get the current single selected plane, or start from first
                    let currentIndex = 0;
                    if (this.selectedBasePlaneIndices.size === 1) {
                        currentIndex = Array.from(this.selectedBasePlaneIndices)[0];
                    }
                    const newIndex = (currentIndex + 1) % this.basePlanes.length;
                    this.selectBasePlane(newIndex);
                }
            }

            // Select all planes (Ctrl/Cmd+A - plain 'a' is the select tool,
            // matching the Java shortcuts)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
                this.selectAllPlanes();
                e.preventDefault();
            }

            // Adjust number of base planes
            if (e.key === '{' || e.key === '<') {
                this.numBasePlanes = Math.max(1, this.numBasePlanes - 1);
                console.log(`Number of base planes: ${this.numBasePlanes}`);
                this.initializeBasePlanes();
                this.regenerateAll(); // layer count is a parametric mutation
            }

            if (e.key === '}' || e.key === '>') {
                this.numBasePlanes = Math.min(20, this.numBasePlanes + 1);
                console.log(`Number of base planes: ${this.numBasePlanes}`);
                this.initializeBasePlanes();
                this.regenerateAll(); // layer count is a parametric mutation
            }

            // Adjust plane spacing (for future cross-plane generation)
            if (e.key === '=' || e.key === '+') {
                this.planeSpacing = Math.min(200, this.planeSpacing + 5);
                console.log(`Plane spacing increased to ${this.planeSpacing}px`);
                this.updateStatusDisplay();
            }

            if (e.key === '-' || e.key === '_') {
                this.planeSpacing = Math.max(10, this.planeSpacing - 5);
                console.log(`Plane spacing decreased to ${this.planeSpacing}px`);
                this.updateStatusDisplay();
            }

            // Save / open (spec §8.4): ⌘S = cloud Save, ⌘⇧S = download a
            // local copy (the old behavior), ⌘O = cloud Open dialog.
            // Without the cloud layer both fall back to the local file ops.
            if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
                if (e.shiftKey || !this.cloudUI) {
                    this.saveDesignToFile();
                } else {
                    this.cloudUI.save();
                }
                e.preventDefault();
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'o' || e.key === 'O')) {
                if (this.cloudUI) this.cloudUI.openDialog();
                else this.loadDesignFromFile();
                e.preventDefault();
            }

            // Export cut pattern SVG (Ctrl/Cmd + E)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'e' || e.key === 'E')) {
                this.exportSVG();
                e.preventDefault();
            }

            // Undo/redo (Ctrl/Cmd + Z, Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
                if (e.shiftKey) {
                    this.redo();
                } else {
                    this.undo();
                }
                e.preventDefault();
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y')) {
                this.redo();
                e.preventDefault();
            }

            // Copy/paste sketches between planes (Ctrl/Cmd + C/V)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
                this.copySketch();
                e.preventDefault();
            }

            if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
                this.pasteSketch();
                e.preventDefault();
            }

            // Escape: cancel in-progress click-based drawing (offset/pen),
            // disarm the interactive slice tool and abandon any rib drag
            if (e.key === 'Escape') {
                const sceneSettings = this.uiManager?.panels?.sceneSettings;
                if (sceneSettings?.visible) {
                    sceneSettings.hide();
                    e.preventDefault();
                    return;
                }
                if (this.sliceTool) {
                    this.setSliceTool(null);
                }
                this.sliceDrag = null;
                this.handGrab = null;
                this.clearMeasurement();
                this.sketchPlane.cancelCurrentDrawing();
                this.removeDrawingPreview();
                e.preventDefault();
            }

            // Enter: finish in-progress click-based drawing (offset/pen)
            if (e.key === 'Enter') {
                this.sketchPlane.finishCurrentDrawing();
                this.removeDrawingPreview();
                e.preventDefault();
            }

            // Phase 3.5: Scene rotation with arrow keys (unified view only)
            if (this.USE_UNIFIED_VIEW && this.sceneRotation) {
                const rotSpeed = this.sceneRotation.getRotationSpeed();

                if (e.key === 'ArrowUp') {
                    this.sceneRotation.rotateX(-rotSpeed);
                    e.preventDefault();
                }
                if (e.key === 'ArrowDown') {
                    this.sceneRotation.rotateX(rotSpeed);
                    e.preventDefault();
                }
                if (e.key === 'ArrowLeft') {
                    this.sceneRotation.rotateY(-rotSpeed);
                    e.preventDefault();
                }
                if (e.key === 'ArrowRight') {
                    this.sceneRotation.rotateY(rotSpeed);
                    e.preventDefault();
                }

                // Reset rotation with 'R'
                if (e.key === 'r' || e.key === 'R') {
                    this.sceneRotation.reset();
                    console.log('Scene rotation reset');
                }
            }
        });
    }

    /**
     * Setup camera controls for unified 3D view (Phase 3.5)
     * Handles mouse interaction with orthographic camera
     */
    setupCameraControls() {
        if (!this.cameraController) return;

        // Active camera mode from the Camera panel ('pan'|'rotate'|'zoom'|null).
        // When set, LEFT-drag controls the camera instead of drawing.
        this.activeCameraMode = null;

        // Mouse state
        let isRightDragging = false;
        let isMiddleDragging = false;
        let leftCameraDrag = null; // Camera mode captured at left-drag start
        let lastMouseX = 0;
        let lastMouseY = 0;

        // Mouse down
        this.canvas.addEventListener('pointerdown', (e) => {
            if (e.button === 2) {
                // Right mouse button - rotate scene (not camera)
                isRightDragging = true;
                lastMouseX = e.clientX;
                lastMouseY = e.clientY;
                e.preventDefault();
            } else if (e.button === 1) {
                // Middle mouse button - pan camera
                isMiddleDragging = true;
                this.cameraController.startDrag(e.clientX, e.clientY, 'pan');
                e.preventDefault();
            } else if (e.button === 0 && this.activeCameraMode) {
                // Left button while a Camera panel mode is active
                leftCameraDrag = this.activeCameraMode;
                if (leftCameraDrag === 'pan') {
                    this.cameraController.startDrag(e.clientX, e.clientY, 'pan');
                }
                lastMouseX = e.clientX;
                lastMouseY = e.clientY;
                e.preventDefault();
            }
        });

        // Mouse move
        this.canvas.addEventListener('pointermove', (e) => {
            if (isRightDragging || leftCameraDrag === 'rotate') {
                // Rotate scene around the chair centre (like arrow keys)
                const deltaX = e.clientX - lastMouseX;
                const deltaY = e.clientY - lastMouseY;

                // Apply rotation to scene (not camera)
                const sensitivity = 0.005; // Radians per pixel
                this.sceneRotation.rotateY(deltaX * sensitivity);
                this.sceneRotation.rotateX(-deltaY * sensitivity); // Invert Y for natural rotation

                lastMouseX = e.clientX;
                lastMouseY = e.clientY;
                e.preventDefault();
            } else if (isMiddleDragging || leftCameraDrag === 'pan') {
                this.cameraController.updateDrag(e.clientX, e.clientY);
                e.preventDefault();
            } else if (leftCameraDrag === 'zoom') {
                const deltaY = e.clientY - lastMouseY;
                this.cameraController.zoomBy(-deltaY * 0.05);
                lastMouseX = e.clientX;
                lastMouseY = e.clientY;
                e.preventDefault();
            }
        });

        // Mouse up
        this.canvas.addEventListener('pointerup', (e) => {
            if (e.button === 2) {
                isRightDragging = false;
            } else if (e.button === 1) {
                isMiddleDragging = false;
                this.cameraController.endDrag();
            } else if (e.button === 0 && leftCameraDrag) {
                if (leftCameraDrag === 'pan') {
                    this.cameraController.endDrag();
                }
                leftCameraDrag = null;
            }
        });

        // Mouse wheel - zoom
        this.canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = -e.deltaY * 0.001; // Negative because deltaY is inverted
            this.cameraController.zoomBy(delta, e.clientX, e.clientY);
        });

        // Disable context menu on canvas
        this.canvas.addEventListener('contextmenu', (e) => {
            e.preventDefault();
        });

        console.log('Camera controls initialized (Phase 3.5)');
        console.log('- Right drag: Rotate scene around chair centre');
        console.log('- Middle drag: Pan view');
        console.log('- Mouse wheel: Zoom');
    }

    /**
     * Set the active Camera panel mode. While a mode is active, left-drag
     * controls the camera instead of drawing. Pass null to return to drawing.
     * @param {string|null} mode - 'pan' | 'rotate' | 'zoom' | null
     */
    setCameraMode(mode) {
        this.activeCameraMode = mode;
        if (this.uiManager?.panels?.camera?.update) {
            this.uiManager.panels.camera.update();
        }
        console.log('Camera mode:', mode || 'off (drawing)');
    }

    /**
     * Toggle the physics collider hull debug draw ('h' key only -
     * developer tooling, deliberately not exposed in the UI).
     * @returns {boolean} New enabled state
     */
    /**
     * Toggle the leg-brace debug overlay ('j'). Draws, per leg:
     * - the leg centre line (yellow)
     * - the seat-anchor acceptance window along the leg (orange band)
     * - every profile crossing candidate (red; the chosen seat anchor green)
     * - the brace beam/drops/crossbrace rectangles BEFORE union (magenta)
     * and emits a structured console log per leg while enabled.
     */
    toggleLegBraceDebug() {
        this.legBraceDebugEnabled = !this.legBraceDebugEnabled;
        console.log('Leg-brace debug overlay:', this.legBraceDebugEnabled ? 'ON' : 'OFF');
        this.renderLegBraceDebug();
        return this.legBraceDebugEnabled;
    }

    /**
     * (Re)draw the leg-brace debug overlay from the records captured by
     * SlicePlaneManager.buildLegSlicePlane. No-op (and cleanup) when the
     * overlay is disabled. Safe to call after every regeneration.
     */
    renderLegBraceDebug() {
        // Clear previous overlay group
        if (this._legDebugGroup) {
            this._legDebugGroup.parent?.remove(this._legDebugGroup);
            this._legDebugGroup.traverse(o => {
                o.geometry?.dispose?.();
                o.material?.dispose?.();
            });
            this._legDebugGroup = null;
        }
        if (!this.legBraceDebugEnabled) return;

        const records = this.slicePlaneManager?.legBraceDebug || [];
        const group = new THREE.Group();
        group.renderOrder = 2000;

        const S = 0.01; // sketch px -> world units
        const lineMat = (color) => new THREE.LineBasicMaterial({
            color, depthTest: false, transparent: true, opacity: 0.95
        });
        const addLine = (pts, color) => {
            const geo = new THREE.BufferGeometry().setFromPoints(pts);
            const line = new THREE.Line(geo, lineMat(color));
            line.renderOrder = 2000;
            group.add(line);
        };
        const addDot = (pos, color, r = 0.035) => {
            const mesh = new THREE.Mesh(
                new THREE.SphereGeometry(r, 8, 8),
                new THREE.MeshBasicMaterial({ color, depthTest: false })
            );
            mesh.position.copy(pos);
            mesh.renderOrder = 2001;
            group.add(mesh);
        };

        for (const d of records) {
            console.log(`[LEG-BRACE DEBUG] leg ${d.legIndex}:`, {
                top: d.top, foot: d.foot,
                legWidth: d.legWidth, capAllowance: d.capAllowance,
                xTop: d.xTop?.toFixed?.(1), xFoot: d.xFoot?.toFixed?.(1),
                crossingsPerPanel: d.crossingsPerPanel,
                crossings: d.crossings.map(c => ({
                    relX: +c.relX.toFixed(1), accepted: c.accepted, chosen: !!c.chosen,
                    at: c.world.map(v => +v.toFixed(2))
                })),
                seatX: d.seatX?.toFixed?.(1),
                seatFallback: d.seatFallback,
                extendToTop: d.extendToTop,
                rects: d.rects.map(r => {
                    const xs = r.map(p => p.x), ys = r.map(p => p.y);
                    return `x ${Math.min(...xs).toFixed(0)}..${Math.max(...xs).toFixed(0)}, y ${Math.min(...ys).toFixed(0)}..${Math.max(...ys).toFixed(0)}`;
                }),
                bail: d.bail
            });

            if (!d.top || !d.foot) continue;

            // Leg centre line (yellow) at z=0 (sketch plane)
            const topV = new THREE.Vector3(d.top.x * S, d.top.y * S, 0);
            const footV = new THREE.Vector3(d.foot.x * S, d.foot.y * S, 0);
            addLine([topV, footV], 0xFFFF00);

            if (d.planePos && d.profileXAxis) {
                const pos = new THREE.Vector3(...d.planePos);
                const ax = new THREE.Vector3(...d.profileXAxis);
                const perp = new THREE.Vector3(-ax.y, ax.x, 0); // in-sketch normal
                const atX = (x) => pos.clone().addScaledVector(ax, x * S);

                // Acceptance window (orange band): xTop-capAllowance .. 0
                // along the leg, band width = legWidth
                const w0 = d.xTop - d.capAllowance;
                const w1 = 0;
                const half = (d.legWidth ?? 20) / 2 * S;
                const c0 = atX(w0), c1 = atX(w1);
                const bandPts = [
                    c0.clone().addScaledVector(perp, half),
                    c1.clone().addScaledVector(perp, half),
                    c1.clone().addScaledVector(perp, -half),
                    c0.clone().addScaledVector(perp, -half),
                    c0.clone().addScaledVector(perp, half)
                ];
                addLine(bandPts, 0xFF8800);

                // Crossing candidates: red dots, chosen anchor green
                for (const c of d.crossings) {
                    addDot(new THREE.Vector3(...c.world), c.chosen ? 0x00FF44 : 0xFF2222,
                        c.chosen ? 0.05 : 0.035);
                }

                // Brace rects BEFORE union (magenta), in the brace plane's
                // profile frame: x along the leg, y = world Z depth
                for (const rectPts of d.rects) {
                    const worldPts = rectPts.map(p =>
                        atX(p.x).add(new THREE.Vector3(0, 0, p.y * S)));
                    worldPts.push(worldPts[0].clone());
                    addLine(worldPts, 0xFF00E5);
                }
            }
        }

        this._legDebugGroup = group;
        this.addOverlay(group);
    }

    togglePhysicsDebug() {
        const enabled = this.physicsDebug.toggle();
        console.log('Physics hull debug draw:', enabled ? 'ON' : 'OFF');
        return enabled;
    }

    /**
     * Toggle floor/ground visibility ('f' key, matches Java's toggleFloor)
     */
    toggleFloor() {
        this.floorVisible = !(this.floorVisible ?? true);

        (this.groundVisuals || []).forEach(mesh => {
            mesh.visible = this.floorVisible;
        });

        const scenePlane = this.sceneManager.scene.children.find(c => c.name === 'groundPlane');
        if (scenePlane) {
            scenePlane.visible = this.floorVisible;
        }

        console.log('Floor', this.floorVisible ? 'shown' : 'hidden');
    }

    /**
     * Export the cut pattern as an SVG file (all parts with slots cut,
     * laid out in rows; units are millimetres)
     * @param {boolean} [download=true] - Trigger a file download
     * @param {Object} [packOpts] - ShapePacker options ({sheetWidth, gap, margin})
     * @returns {{svg: string, partCount: number}|null}
     */
    exportSVG(download = true, packOpts = {}) { return this.designIO.exportSVG(download, packOpts); }

    /**
     * Export the cut pattern as a DXF R12 file (CNC/laser toolchains)
     * @param {boolean} [download=true]
     * @param {Object} [packOpts] - ShapePacker options ({sheetWidth, gap, margin})
     * @returns {{dxf: string, partCount: number}|null}
     */
    exportDXF(download = true, packOpts = {}) { return this.designIO.exportDXF(download, packOpts); }

    /**
     * Serialize the whole design to a plain JSON object
     */
    serializeDesign() { return this.designIO.serializeDesign(); }

    /**
     * Load a serialized design (replaces the current sketch)
     * @param {Object} data - Object produced by serializeDesign()
     * @returns {boolean} True on success
     */
    loadDesign(data) { return this.designIO.loadDesign(data); }

    /**
     * Download the design as a .sketchchair.json file (Ctrl+S)
     */
    saveDesignToFile() { return this.designIO.saveDesignToFile(); }

    /**
     * Open a design file via file picker (Ctrl+O)
     */
    loadDesignFromFile() { return this.designIO.loadDesignFromFile(); }

    /**
     * Autosave to localStorage (debounced; called on every shape change).
     * Also feeds the cloud layer: dirty flag + debounced working-draft
     * push when signed in (never a version - spec D2).
     */
    autosaveDesign() {
        this.cloudUI?.onLocalChange();
        return this.designIO.autosaveDesign();
    }

    /**
     * PNG thumbnail of the current 3D view (cloud Save/Publish)
     * @returns {string|null} data URL
     */
    captureThumbnail() { return this.sceneManager?.captureThumbnail?.() || null; }

    /**
     * Restore the last autosaved design, if any (called at startup,
     * matching the Java version's autoLoad)
     */
    restoreAutosave() { return this.designIO.restoreAutosave(); }

    // --- Undo/redo (delegates to UndoManager) ---

    /** Snapshot the current committed sketch state for undo */
    pushUndoSnapshot() { return this.undoManager.pushUndoSnapshot(); }

    /** Discard the most recent snapshot (grab attempts that grabbed nothing) */
    discardLastUndoSnapshot() { return this.undoManager.discardLastUndoSnapshot(); }

    /**
     * Clear the whole design (toolbar Clear button). Undoable.
     */
    clearAll() {
        this.pushUndoSnapshot();
        // Explicitly clear the SELECTED layers' stroke instances too:
        // the sync diff deliberately preserves detached copies on other
        // layers, but Clear means "empty everything I have selected"
        this.strokeStore?.clearLayers([...this.selectedBasePlaneIndices]);
        this.sketchPlane.restoreState({ paths: [], splines: [], closedShapes: [], legShapes: [] });
        console.log('Design cleared');
    }

    /** Undo the last committed sketch mutation (Ctrl+Z / toolbar) */
    undo() { return this.undoManager.undo(); }

    /** Redo the last undone mutation (Ctrl+Shift+Z / Ctrl+Y) */
    redo() { return this.undoManager.redo(); }

    /**
     * Recentre the scene-rotation pivot on the chair's bounding-box centre
     * so rotation turns the model in place instead of orbiting the world
     * origin. Called after mesh regeneration.
     *
     * Ignored WHILE a regeneration is in flight: the rib meshes are gone for
     * those frames, and a pivot measured on the panels alone sends the whole
     * scene lurching in a rotated view (see regenerateAll). regenerateAll
     * recentres once the complete mesh set is back.
     */
    updateRotationPivot() {
        if (this._pivotFreezeDepth > 0) return;
        if (!this.sceneRotation || !this.generatedMeshes || this.generatedMeshes.length === 0) {
            return;
        }

        const box = new THREE.Box3();
        for (const mesh of this.generatedMeshes) {
            if (!mesh.geometry) continue;
            mesh.updateMatrix();
            if (!mesh.geometry.boundingBox) {
                mesh.geometry.computeBoundingBox();
            }
            // Mesh positions are local to the pivot group, so transform each
            // geometry bbox by the mesh's LOCAL matrix (not matrixWorld,
            // which would double-apply the pivot group's rotation)
            const childBox = mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrix);
            box.union(childBox);
        }

        if (!box.isEmpty()) {
            const center = box.getCenter(new THREE.Vector3());
            this.sceneRotation.setPivotPoint(center);
        }
    }

    // =========================================================================
    // Interactive slice placement / editing (matches the Java slice tools:
    // UITools.SLICES_SINGLE_SLICE / SLICES_GROUP_SLICES / SLICE_EDIT_MODE_POS)
    // =========================================================================

    /**
     * Arm or disarm the interactive slice placement tool.
     * While armed, left-click on the sketch places slices (single slice:
     * one click; slice group: two clicks on the same shape) instead of
     * drawing. 'edit' arms rib repositioning: click-drag an existing rib to
     * move it (or its group) along the guide path. Escape disarms.
     * @param {string|null} type - 'single' | 'group' | 'edit' | null (disarm)
     */
    setSliceTool(type) {
        this.sliceTool = type ? { type, pending: null } : null;
        this.sliceDrag = null;
        this.clearSlicePreview();

        // Reflect armed state on the Slices panel buttons
        const panel = this.uiManager?.panels?.slices;
        if (panel?.onSliceToolChanged) {
            panel.onSliceToolChanged(this.sliceTool);
        }
        console.log('Slice tool:', type ? `armed (${type})` : 'off');
    }

    /**
     * =====================================================================
     * CANONICAL PARAMETRIC REGENERATION PIPELINE
     * =====================================================================
     * The ONE entry point for rebuilding the chair after ANY mutation:
     * shape draw/edit/delete, leg edits (incl. per-point widths), layer
     * add/remove/select, chair width, material thickness, slice-group
     * option edits, paste, save/load, undo/redo. Do NOT call the stage
     * functions directly from mutation sites - route through here so every
     * dependent artifact (leg braces included) is rebuilt consistently.
     *
     * Stages, in dependency order. The async tail runs under the
     * _crossSliceGeneration stale guard: a superseding edit bumps the
     * generation and the older run aborts cleanly mid-pipeline.
     *
     *  1. SYNC    live sketch -> per-plane sketch states + width widget
     *             (updateSketchRendering). Skippable via {syncSketch:false}
     *             for mutations that already wrote the plane states.
     *  2. SLICES  autoCreatePrimarySlicePlane -> autoGenerateCrossSlices
     *             (sync prefix updates primary plane + basePlane sketches,
     *             then async):
     *       2a. LEG BRACES first (generateLegCrossSlices) - leg planes are
     *           in the manager before the groups run, so
     *           removeCollidingRibs culls regular ribs that hit them
     *           (leg slices WIN, Java tiedToLeg priority)
     *       2b. slice groups (ribs/slats; oblique/sliver/colliding culled)
     *       2c. waffle slots (SlicePlaneManager.addPlane -> generateSlots,
     *           incl. the leg-extension slot pairs)
     *       2d. panel meshes (stroke+leg union, slot subtraction) and rib
     *           meshes (regenerateMeshesFromCurrentSketch +
     *           generateMeshesForAllSlicePlanes)
     *       2e. compound physics body (createCompoundChairPhysicsBody)
     *       2f. slices panel + leg-brace debug overlay refresh
     *  3. PANELS  immediate synchronous panel-mesh rebuild for visual
     *             feedback while the async slice pipeline runs
     *  4. UI      autosave (debounced), cut-pattern preview refresh,
     *             left-panel visibility
     *
     * @param {Object} [opts]
     * @param {boolean} [opts.syncSketch=true] - sync the live sketch into
     *        the selected planes' states first
     * @returns {Promise} resolves when the async pipeline settles (callers
     *          that need the final physics/mesh state MUST await; fire-and-
     *          forget is fine for interactive edits)
     */
    regenerateAll({ syncSketch = true } = {}) {
        // The rib meshes are torn down here and only come back in the async
        // tail, so for a few frames the chair is PANELS ONLY. The rotation
        // pivot is the centre of the mesh bounding box, and the leg braces
        // reach down to the feet - without them that centre leaps ~3 units up
        // the chair. Recentring on that partial set moved the pivot, and since
        // the pivot group's position is C - R*C, a rotated view then translated
        // the whole scene (measured: 1.3 units) and snapped back once the ribs
        // returned. That is the chair/camera lurching mid-rebuild.
        //
        // So freeze the pivot for the duration and recentre ONCE, on the
        // complete mesh set. Counted, because regenerations can nest.
        this._pivotFreezeDepth = (this._pivotFreezeDepth || 0) + 1;

        // 1. SYNC
        if (syncSketch) {
            this.updateSketchRendering();
        }

        // 2. SLICES (async tail; sync prefix updates plane sketches first)
        const pipeline = this.autoCreatePrimarySlicePlane();

        // 3. PANELS - immediate feedback from the just-synced states
        this.regenerateMeshesFromCurrentSketch();

        // 4. UI
        this.autosaveDesign();
        this.uiManager?.panels?.materials?.refreshPattern?.();
        this.uiManager?.updateLeftPanelVisibility?.();

        const thaw = () => {
            this._pivotFreezeDepth = Math.max(0, (this._pivotFreezeDepth || 1) - 1);
            if (this._pivotFreezeDepth === 0) this.updateRotationPivot();
        };
        return Promise.resolve(pipeline).then(
            (v) => { thaw(); return v; },
            (err) => { thaw(); throw err; }
        );
    }

    /**
     * Trigger a full slice+mesh regeneration through the canonical
     * pipeline (async and stale-guarded via _crossSliceGeneration).
     * Thin wrapper kept for existing callers.
     */
    regenerateSliceGroups() {
        return this.regenerateAll();
    }

    /**
     * Set the material thickness (mm) for the whole design: applied to all
     * existing base planes and rib planes, and used as the default for any
     * newly created planes. Triggers a full regeneration because slot
     * widths derive from the intersecting part's thickness.
     * @param {number} mm - New material thickness in millimetres
     */
    setMaterialThickness(mm) {
        const t = Math.max(0.1, Number(mm) || 0.1);
        this.materialThickness = t;
        console.log(`Material thickness (design) set to ${t}mm - regenerating`);
        this.applyEffectiveThickness();
    }

    /**
     * The thickness actually used by planes/slots/exports: the Make
     * panel's fabrication OVERRIDE when set, else the design value from
     * the Layers section. Precedence: fabSettings.thicknessOverride >
     * materialThickness.
     * @returns {number} mm
     */
    getEffectiveThickness() {
        const o = this.fabSettings?.thicknessOverride;
        return (Number.isFinite(o) && o > 0) ? o : this.materialThickness;
    }

    /**
     * Set (or clear, with null) the fabrication-only thickness override.
     * The design value (materialThickness / Layers panel) is untouched;
     * planes, slot widths and exports regenerate with the override so
     * fabrication output matches the target stock.
     * @param {number|null} mm
     */
    setFabThicknessOverride(mm) {
        const v = Number(mm);
        this.fabSettings.thicknessOverride = (Number.isFinite(v) && v > 0) ? v : null;
        console.log('Fabrication thickness override:',
            this.fabSettings.thicknessOverride ?? '(none - using design value)');
        this.applyEffectiveThickness();
    }

    /** Push the effective thickness to every plane and regenerate */
    applyEffectiveThickness() {
        const t = this.getEffectiveThickness();
        for (const plane of this.basePlanes || []) {
            plane.thickness = t;
        }
        for (const plane of this.slicePlaneManager?.planes || []) {
            plane.thickness = t;
        }
        this.regenerateSliceGroups();
    }

    /**
     * Add a 2D-sketch overlay object (stroke outline, control points,
     * drawing/slice preview) to the shared overlay frame so it follows the
     * chair's physics pose. Falls back to the plain scene add in legacy mode.
     * @param {THREE.Object3D} object
     */
    addOverlay(object) {
        if (this.overlayFrame) {
            this.overlayFrame.add(object);
        } else {
            this.sceneManager.add(object);
        }
    }

    /**
     * The chair's CURRENT rigid transform: the rendered pose relative to the
     * generated (design) frame that SlicePlane positions - and therefore the
     * flat-parts preview's assembled poses, and the sketch overlay - are
     * expressed in. Composed of the hand-tool parking and anything the
     * simulation has done to the body, exactly as syncMeshes applies it:
     *
     *   rendered = delta.quaternion * design + delta.position
     *
     * Identity when the chair sits at its generated pose. Anything that has
     * to line up with the chair AS DRAWN must go through this, or it lands at
     * the design pose while the chair is somewhere else.
     *
     * @returns {{position: THREE.Vector3, quaternion: THREE.Quaternion}}
     */
    getChairFrameDelta() {
        const delta = this.physicsWorld?.getCompoundBodyDelta?.() ||
            (this.chairMoveOffset.lengthSq() > 0
                ? { position: this.chairMoveOffset.clone(), quaternion: new THREE.Quaternion() }
                : null);
        return delta || {
            position: new THREE.Vector3(),
            quaternion: new THREE.Quaternion()
        };
    }

    /**
     * Per-frame update of the chair's physics frame delta (current compound
     * body pose relative to its pose at body creation) and of the overlay
     * frame that carries all 2D sketch overlays.
     *
     * Active from physics play until reset/restore - kept while merely
     * paused so the overlay stays aligned with the still-displaced meshes
     * (syncMeshes keeps applying the body pose every frame regardless).
     */
    updatePhysicsFrameDelta() {
        this.physicsFrameDelta = this.physicsOverlayActive
            ? this.physicsWorld.getCompoundBodyDelta()
            : null;

        // Regeneration tears the compound body down and rebuilds it in an async
        // tail, so there is no body to read a delta from for a few frames. Fall
        // back to the parked offset (the meshes are held there by
        // parkGeneratedMeshes) - otherwise the overlay flashes back to the
        // chair's generated pose while the chair itself stays put.
        if (!this.physicsFrameDelta && this.chairMoveOffset.lengthSq() > 0) {
            this.physicsFrameDelta = {
                position: this.chairMoveOffset.clone(),
                quaternion: new THREE.Quaternion()
            };
        }

        if (this.overlayFrame) {
            if (this.physicsFrameDelta) {
                this.overlayFrame.position.copy(this.physicsFrameDelta.position);
                this.overlayFrame.quaternion.copy(this.physicsFrameDelta.quaternion);
            } else {
                this.overlayFrame.position.set(0, 0, 0);
                this.overlayFrame.quaternion.identity();
            }
        }
    }

    /**
     * Ray cast a pointer event to sketch-pixel coordinates on the first
     * selected base plane hit.
     * @param {PointerEvent} e
     * @returns {{x: number, y: number}|null} Sketch pixels, or null if no hit
     */
    raycastSketchPoint(e) {
        const intersections = PlaneIntersector.getSelectedPlaneIntersections(
            e.clientX,
            e.clientY,
            this.basePlanes,
            this.selectedBasePlaneIndices,
            this.cameraController.camera,
            this.canvas,
            this.sceneRotation?.getPivotGroup(),
            this.physicsFrameDelta
        );
        if (intersections.length === 0) return null;
        return {
            x: intersections[0].intersection.x * 100,
            y: intersections[0].intersection.y * 100
        };
    }

    /**
     * Resolve the guide path for a shape index against the LIVE sketch,
     * mirroring resolveCrossSliceGuide: non-leg spline centreline first,
     * closed shape outline as fallback.
     * @param {number} index - shapeIndex
     * @returns {Object|null} A path with getPos(t), or null
     */
    resolveGuidePathByIndex(index) {
        const splines = this.sketchPlane.splines || [];
        const closedShapes = this.sketchPlane.closedShapes || [];

        const spline = splines[index];
        if (spline && spline.type !== SketchSpline.TYPE_LEG &&
            spline.centrePath && spline.centrePath.points && spline.centrePath.points.length > 0) {
            return spline.centrePath;
        }

        const shape = closedShapes[index];
        if (shape) {
            const path = (typeof shape.getPos === 'function') ? shape : (shape.path || null);
            if (path && typeof path.getPos === 'function') return path;
        }
        return null;
    }

    /**
     * Find the closest point (parameter t) on a guide path to a sketch point
     * by sampling ~100 params along the path.
     * @param {Object} path - Path with getPos(t)
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     * @returns {{t: number, distance: number}|null}
     */
    closestPointOnGuide(path, x, y) {
        const SAMPLES = 100;
        let best = null;
        for (let s = 0; s <= SAMPLES; s++) {
            const t = s / SAMPLES;
            const p = path.getPos(t);
            if (!p) continue; // getPos can return null at the extremes
            const d = Math.hypot(p.x - x, p.y - y);
            if (!best || d < best.distance) {
                best = { t, distance: d };
            }
        }
        return best;
    }

    /**
     * Find the sketch shape whose guide path is nearest to a click.
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     * @param {number} maxDist - Ignore shapes farther than this (sketch px)
     * @returns {{shapeIndex: number, t: number, distance: number}|null}
     */
    findNearestGuideShape(x, y, maxDist = 80) {
        const splines = this.sketchPlane.splines || [];
        const closedShapes = this.sketchPlane.closedShapes || [];
        const count = Math.max(splines.length, closedShapes.length);

        let best = null;
        for (let i = 0; i < count; i++) {
            const guide = this.resolveGuidePathByIndex(i);
            if (!guide) continue;
            const near = this.closestPointOnGuide(guide, x, y);
            if (near && (!best || near.distance < best.distance)) {
                best = { shapeIndex: i, t: near.t, distance: near.distance };
            }
        }
        return (best && best.distance <= maxDist) ? best : null;
    }

    /**
     * Handle a click while the interactive slice tool is armed.
     * Single slice: place at the nearest point on the nearest shape, disarm.
     * Slice group: first click stores an anchor; second click on the SAME
     * shape creates the group between the two params. A second click on a
     * DIFFERENT shape restarts the pending anchor there.
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     */
    handleSliceToolPointerDown(x, y) {
        if (!this.sliceTool) return;

        const hit = this.findNearestGuideShape(x, y, 80);
        if (!hit) {
            console.log('Slice tool: no shape within range of click');
            return;
        }

        const panel = this.uiManager?.panels?.slices;
        if (!panel) return;

        // getPos(t) can return null at exactly t=1, keep params just inside
        const t = Math.min(0.999, Math.max(0, hit.t));

        if (this.sliceTool.type === 'single') {
            panel.createSliceSelection(SliceType.SINGLE_SLICE, {
                start: t,
                end: t,
                spacing: 0,
                shapeIndex: hit.shapeIndex
            });
            this.setSliceTool(null);
        } else if (this.sliceTool.type === 'group') {
            const pending = this.sliceTool.pending;

            if (pending && pending.t2 !== undefined) {
                // THIRD click: confirm with the current density
                panel.createSliceSelection(SliceType.SLICES, {
                    start: Math.min(pending.t, pending.t2),
                    end: Math.max(pending.t, pending.t2),
                    spacing: this.sliceTool.spacing || 50,
                    shapeIndex: pending.shapeIndex
                });
                this.setSliceTool(null);
            } else if (pending && pending.shapeIndex === hit.shapeIndex) {
                // SECOND click: fix the end, enter density phase - mouse
                // distance from this point maps to slice spacing
                pending.t2 = t;
                pending.endPt = { x, y };
                this.sliceTool.spacing = 50;
                console.log('Slice group: end set - move to adjust density, click to confirm');
            } else {
                // FIRST click (or click on a different shape): (re)anchor
                this.sliceTool.pending = { shapeIndex: hit.shapeIndex, t };
                console.log(`Slice group: anchored on shape ${hit.shapeIndex} at t=${t.toFixed(3)} - click a second point on the same shape`);
            }
        }
    }

    /**
     * Hover preview for the armed slice tool: shows where the slice(s)
     * would land, snapped to the nearest shape. In the group tool's density
     * phase, mouse distance from the end anchor maps to slice spacing and
     * all resulting slice positions are previewed live.
     * @param {PointerEvent} e
     */
    updateSlicePreview(e) {
        if (!this.sliceTool) return;
        const pt = this.raycastSketchPoint(e);
        if (!pt) { this.clearSlicePreview(); return; }

        // Edit mode: highlight the rib chord under the cursor (magenta)
        if (this.sliceTool.type === 'edit') {
            if (this.sliceDrag) { this.clearSlicePreview(); return; }
            const grabRadius = 12 / (0.01 * this.cameraController.zoom);
            const hit = this.findRibAt(pt.x, pt.y, Math.max(10, grabRadius));
            const chord = hit ? this.getRibChordSketch(hit.plane) : null;
            this.renderSlicePreview(chord ? [chord] : [], 0xFF00E5);
            return;
        }

        const pending = this.sliceTool.pending;
        const chords = []; // {shapeIndex, t}

        if (pending && pending.t2 !== undefined) {
            // Density phase: spacing = distance from the end anchor (sketch px)
            const dist = Math.hypot(pt.x - pending.endPt.x, pt.y - pending.endPt.y);
            this.sliceTool.spacing = Math.min(200, Math.max(15, Math.round(dist) || 50));

            const guide = this.resolveGuidePathByIndex(pending.shapeIndex);
            if (guide) {
                const len = this.guidePathLength(guide);
                const t1 = Math.min(pending.t, pending.t2);
                const t2 = Math.max(pending.t, pending.t2);
                const span = Math.max(1e-6, (t2 - t1) * len);
                const count = Math.min(60, Math.max(2, Math.floor(span / this.sliceTool.spacing) + 1));
                for (let i = 0; i < count; i++) {
                    chords.push({ shapeIndex: pending.shapeIndex, t: t1 + (t2 - t1) * (i / (count - 1)) });
                }
            }
        } else {
            const hit = this.findNearestGuideShape(pt.x, pt.y, 80);
            if (hit) {
                chords.push({ shapeIndex: hit.shapeIndex, t: hit.t });
            }
            if (pending) {
                chords.push({ shapeIndex: pending.shapeIndex, t: pending.t });
            }
        }

        this.renderSlicePreview(chords);
    }

    /** Approximate guide path length in sketch px */
    guidePathLength(guide) {
        let len = 0;
        let prev = guide.getPos(0);
        for (let i = 1; i <= 60; i++) {
            const cur = guide.getPos(Math.min(0.999, i / 60));
            if (prev && cur) len += Math.hypot(cur.x - prev.x, cur.y - prev.y);
            prev = cur;
        }
        return len || 1;
    }

    /**
     * Draw preview chord lines (perpendicular to the guide) at the given
     * path positions. Entries can also be raw sketch-px segments
     * ({ax, ay, bx, by}, e.g. a rib chord from getRibChordSketch).
     * @param {Array<{shapeIndex, t}|{ax, ay, bx, by}>} chords
     * @param {number} [color] - Line color (default cyan; edit mode uses magenta)
     */
    renderSlicePreview(chords, color = 0x00E5FF) {
        this.clearSlicePreview();
        if (!chords.length) return;

        const group = new THREE.Group();
        const zTop = Math.max(...this.basePlanes.map(b => b.position.z)) + 0.03;

        const addLine = (x0, y0, x1, y1) => {
            const geo = new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(x0, y0, zTop),
                new THREE.Vector3(x1, y1, zTop)
            ]);
            const mat = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.9 });
            const line = new THREE.Line(geo, mat);
            line.renderOrder = 998;
            group.add(line);
        };

        for (const chord of chords) {
            // Raw sketch-px segment (rib chord highlight in edit mode)
            if (chord.ax !== undefined) {
                addLine(chord.ax * 0.01, chord.ay * 0.01, chord.bx * 0.01, chord.by * 0.01);
                continue;
            }

            const { shapeIndex, t } = chord;
            const guide = this.resolveGuidePathByIndex(shapeIndex);
            if (!guide) continue;
            const tt = Math.min(0.989, Math.max(0.01, t));
            const p0 = guide.getPos(tt - 0.01);
            const p1 = guide.getPos(tt + 0.01);
            const pos = guide.getPos(tt);
            if (!p0 || !p1 || !pos) continue;

            let tx = p1.x - p0.x, ty = p1.y - p0.y;
            const tl = Math.hypot(tx, ty) || 1;
            // Perpendicular to the tangent = the chord direction
            const px = -ty / tl, py = tx / tl;

            const spline = this.sketchPlane.splines[shapeIndex];
            const half = ((spline && spline.type !== SketchSpline.TYPE_LEG ? spline.offsetSize * 2 : 80) / 2 + 12) * 0.01;

            addLine(
                pos.x * 0.01 - px * half, pos.y * 0.01 - py * half,
                pos.x * 0.01 + px * half, pos.y * 0.01 + py * half
            );
        }

        this.slicePreviewGroup = group;
        this.addOverlay(group);
    }

    /** Remove the slice placement preview */
    clearSlicePreview() {
        if (this.slicePreviewGroup) {
            this.slicePreviewGroup.parent?.remove(this.slicePreviewGroup);
            this.slicePreviewGroup.traverse(o => {
                if (o.geometry) o.geometry.dispose();
                if (o.material) o.material.dispose();
            });
            this.slicePreviewGroup = null;
        }
    }

    /**
     * Rib chord in sketch XY (sketch px): plane.position +- profileXAxis *
     * profile X extent. Same math as SlicePlaneManager.removeCollidingRibs.
     * @param {SlicePlane} plane - A cross-slice (rib) plane
     * @returns {{ax,ay,bx,by}|null} Chord endpoints in sketch px
     */
    getRibChordSketch(plane) {
        const pts = plane.sketch?.closedShapes?.[0]?.points;
        if (!pts || pts.length === 0 || !plane.profileXAxis) return null;
        const xs = pts.map(q => q.x);
        const a = plane.position.clone().addScaledVector(plane.profileXAxis, Math.min(...xs) * 0.01);
        const b = plane.position.clone().addScaledVector(plane.profileXAxis, Math.max(...xs) * 0.01);
        return { ax: a.x * 100, ay: a.y * 100, bx: b.x * 100, by: b.y * 100 };
    }

    /**
     * Find the rib (cross-slice plane) whose chord is nearest a sketch point.
     * Only ribs belonging to editable slice groups (SLICES / SINGLE_SLICE)
     * are considered.
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     * @param {number} maxDist - Max distance to the chord (sketch px)
     * @returns {{plane, selection, distance}|null}
     */
    findRibAt(x, y, maxDist = 10) {
        const pointSegDist = (px, py, ax, ay, bx, by) => {
            const dx = bx - ax, dy = by - ay;
            const len2 = dx * dx + dy * dy;
            const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
            return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
        };

        let best = null;
        for (const plane of this.slicePlaneManager.planes) {
            const sel = plane.crossSliceSelection;
            if (!plane.profileXAxis || !sel) continue;
            if (sel.type !== SliceType.SLICES && sel.type !== SliceType.SINGLE_SLICE) continue;

            const chord = this.getRibChordSketch(plane);
            if (!chord) continue;

            const d = pointSegDist(x, y, chord.ax, chord.ay, chord.bx, chord.by);
            if (d <= maxDist && (!best || d < best.distance)) {
                best = { plane, selection: sel, distance: d };
            }
        }
        return best;
    }

    /**
     * Start a click-drag edit of an existing rib (SLICE_EDIT_MODE_POS).
     * Which parameter moves depends on the grabbed rib:
     * - SINGLE_SLICE: start=end follow the cursor
     * - first/last rib of a SLICES group: start / end follows the cursor
     * - middle rib: the WHOLE group translates along the guide path
     * @param {{plane, selection}} hit - Result of findRibAt
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     * @returns {boolean} True if the drag started
     */
    beginSliceDrag(hit, x, y) {
        const sel = hit.selection;
        const guide = this.resolveGuidePathByIndex(sel.shapeIndex ?? 0);
        if (!guide) return false;

        let mode = 'single';
        if (sel.type === SliceType.SLICES) {
            const planes = sel.planes || [];
            const idx = planes.indexOf(hit.plane);
            if (planes.length > 1 && idx === 0) {
                mode = 'start';
            } else if (planes.length > 1 && idx === planes.length - 1) {
                mode = 'end';
            } else {
                // Dragging a middle rib adjusts the group's SPACING: the
                // grabbed rib follows the cursor by rescaling the density
                // (Java's SLICE_EDIT_MODE_SPACING)
                mode = 'spacing';
            }
        }

        const near = this.closestPointOnGuide(guide, x, y);
        this.sliceDrag = {
            selection: sel,
            mode,
            ribIndex: sel.planes ? Math.max(1, sel.planes.indexOf(hit.plane)) : 1,
            guideLen: this.guidePathLength(guide),
            grabT: near ? near.t : sel.start,
            origStart: sel.start,
            origEnd: sel.end,
            lastRegen: 0
        };
        console.log(`Grabbed rib of slice group (mode: ${mode}, start=${sel.start.toFixed(3)}, end=${sel.end.toFixed(3)})`);
        return true;
    }

    /**
     * Update an in-progress rib drag: move start/end (or the whole group)
     * to the cursor's parameter on the guide path and regenerate, throttled
     * to at most one regeneration per 150ms (pointerup always regenerates).
     * @param {number} x - Sketch px
     * @param {number} y - Sketch px
     */
    updateSliceDrag(x, y) {
        const drag = this.sliceDrag;
        if (!drag) return;

        const sel = drag.selection;
        const guide = this.resolveGuidePathByIndex(sel.shapeIndex ?? 0);
        if (!guide) return;

        const near = this.closestPointOnGuide(guide, x, y);
        if (!near) return;

        const T_MAX = 0.999; // getPos(1) can return null
        const t = Math.min(T_MAX, Math.max(0, near.t));

        if (drag.mode === 'single') {
            sel.start = t;
            sel.end = t;
        } else if (drag.mode === 'start') {
            sel.start = Math.min(t, sel.end);
        } else if (drag.mode === 'end') {
            sel.end = Math.max(t, sel.start);
        } else if (drag.mode === 'spacing') {
            // Solve spacing so the grabbed rib (index k from the start)
            // lands under the cursor: spacing = (t - start) * pathLen / k.
            // Start stays anchored; the rib count adjusts with density.
            const along = Math.max(0.005, t - sel.start) * drag.guideLen;
            sel.spacing = Math.min(200, Math.max(15, along / drag.ribIndex));
        } else { // 'whole' - translate the group, clamped to [0, T_MAX]
            let delta = t - drag.grabT;
            delta = Math.max(-drag.origStart, Math.min(T_MAX - drag.origEnd, delta));
            sel.start = drag.origStart + delta;
            sel.end = drag.origEnd + delta;
        }

        const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
        if (now - drag.lastRegen >= 150) {
            drag.lastRegen = now;
            this.regenerateSliceGroups();
        }
    }

    /**
     * Finish a rib drag: one final (non-throttled) regeneration.
     */
    endSliceDrag() {
        if (!this.sliceDrag) return;
        this.sliceDrag = null;
        this.regenerateSliceGroups();
        console.log('Rib drag finished');
    }

    // =====================================================================
    // HAND TOOL - grab and drag the chair during physics play.
    // Approach: an impulse-based SPRING toward the cursor (applied at the
    // grab point every physics step) rather than a kinematic joint -
    // rapier3d-compat mouse-pick joints need create/remove churn per grab
    // and fight the compound body's sleeping, while a critically-damped
    // spring impulse is stable, tunes with body mass, and lets the chair
    // dangle and swing naturally under the cursor.
    // =====================================================================

    /**
     * Try to grab the chair or the figure under the pointer (hand tool
     * pointer-down). Both are grabbable whether or not physics is playing:
     * playing -> spring drag through the solver; paused -> kinematic CARRY
     * (see below).
     */
    beginHandGrab(e, { figureOnly = false, silent = false } = {}) {
        // Raycast the pointer against grabbable meshes (world space):
        // the chair panels/ribs and the ragdoll figure's body parts.
        const figureMeshes = (this.ragdoll?.visible ? this.ragdoll.meshes : [])
            .filter(m => !!m);
        const targets = figureOnly
            ? figureMeshes
            : [...this.generatedMeshes, ...figureMeshes];
        if (targets.length === 0) {
            if (!silent) this.showToast('Nothing to grab yet');
            return false;
        }

        const rect = this.canvas.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, this.cameraController.camera);
        const hits = raycaster.intersectObjects(targets, false);
        if (hits.length === 0) return false; // empty click: leave camera alone

        let hitMesh = hits[0].object;
        let hitWorld = hits[0].point.clone();
        const isFigure = hitMesh.userData?.ragdollPart !== undefined;

        // A paused chair grab AUTHORS the placement, so it must start from the
        // authored pose: if a physics run left the chair tipped or slid, put it
        // back upright at its parked spot first. Otherwise the drag would move
        // a fallen chair while the offset (which is saved with the design)
        // accumulated from the design's own pose - screen and file disagreeing.
        // The chair moves out from under the cursor, so re-raycast to grab it
        // where it now is; if the snap moved it clear of the pointer, the
        // original hit still serves (a carry drag is relative, so it only sets
        // the drag plane).
        if (!isFigure && !this.isPhysicsPlaying && this.snapChairToAuthoredPose()) {
            this.physicsWorld.syncMeshes();
            const reHits = raycaster.intersectObjects(targets, false);
            const reChair = reHits.find(h => h.object.userData?.ragdollPart === undefined);
            if (reChair) {
                hitMesh = reChair.object;
                hitWorld = reChair.point.clone();
            }
        }

        // Resolve the physics body for the hit:
        // - figure parts map mesh -> body via physicsWorld.bodies, and
        //   live in the WORLD frame (their meshes are not in the pivot)
        // - the chair is the compound body, in the rotation pivot's LOCAL
        //   frame (the view rotation is purely visual)
        let body = null;
        let usePivot = false;
        if (isFigure) {
            body = this.physicsWorld.bodies.get(hitMesh) || null;
        } else {
            body = this.physicsWorld.compoundBodies?.[0]?.body || null;
            usePivot = true;
        }
        if (!body) {
            if (!silent) this.showToast('No physics body to grab');
            return false;
        }

        const pivot = this.sceneRotation?.getPivotGroup();
        if (pivot) pivot.updateMatrixWorld(true);
        const pivotInv = (usePivot && pivot)
            ? pivot.matrixWorld.clone().invert()
            : new THREE.Matrix4();
        const hitLocal = hitWorld.clone().applyMatrix4(pivotInv);

        const pos = body.translation();
        const rot = body.rotation();
        const invQ = new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w).invert();
        const localOffset = hitLocal.clone()
            .sub(new THREE.Vector3(pos.x, pos.y, pos.z))
            .applyQuaternion(invQ);

        // Camera-parallel drag plane through the grab point (world space)
        const camDir = new THREE.Vector3();
        this.cameraController.camera.getWorldDirection(camDir);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, hitWorld);

        this.handGrab = {
            body,
            isFigure,
            usePivot,
            localOffset,
            plane,
            targetLocal: hitLocal.clone(),
            // Paused grabs CARRY kinematically instead of spring-dragging
            // through the solver: the whole figure for a figure hit, the
            // whole chair (compound body) for a chair hit. With the world
            // not stepping this is a free move - no gravity, no collisions,
            // no rotation.
            carry: !this.isPhysicsPlaying,
            prevTarget: hitLocal.clone(),
            // Chair carry stays in the drawing plane: depth is pinned to
            // the grab point, so the drag is pure XY (see updateHandGrabTarget)
            lockZ: hitLocal.z,
            // Screen-space press point: a release close to here is a CLICK
            // (selects the figure) rather than a drag/carry.
            downX: e.clientX,
            downY: e.clientY
        };
        body.wakeUp();

        // A carried chair displaces the meshes from their generated pose, so
        // the sketch overlay and pointer raycasting have to track the chair's
        // body delta - the same frame the simulation uses.
        if (this.handGrab.carry && !isFigure) this.physicsOverlayActive = true;

        console.log(`[HAND] Grabbed the ${isFigure ? 'figure' : 'chair'} at`,
            hitLocal.toArray().map(v => v.toFixed(2)),
            this.handGrab.carry ? '(carry mode)' : '(spring mode)');
        return true;
    }

    /** Update the hand-grab spring target from a pointer event */
    updateHandGrabTarget(e) {
        const g = this.handGrab;
        if (!g) return;
        const rect = this.canvas.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, this.cameraController.camera);
        const target = new THREE.Vector3();
        if (!raycaster.ray.intersectPlane(g.plane, target)) return;
        if (g.usePivot) {
            const pivot = this.sceneRotation?.getPivotGroup();
            if (pivot) pivot.updateMatrixWorld(true);
            const pivotInv = pivot
                ? pivot.matrixWorld.clone().invert()
                : new THREE.Matrix4();
            target.applyMatrix4(pivotInv);
        }
        if (!g.carry) {
            g.targetLocal = target;
            return;
        }

        // CARRY mode (figure, physics paused): translate the whole figure
        // rigidly with the cursor and sync its meshes right away
        if (g.isFigure) {
            g.targetLocal = target;
            if (this.ragdoll) {
                const d = target.clone().sub(g.prevTarget);
                this.ragdoll.translateAll(d.x, d.y, d.z);
                g.prevTarget = target.clone();
            }
            return;
        }

        // CARRY mode (chair, physics paused): move the compound body itself,
        // not the meshes - syncMeshes() re-applies the body pose every frame
        // whether or not the world is stepping, so a direct mesh write would
        // be clobbered. The world is not stepping, so this is a free move.
        // Depth is pinned to the grab point (pure XY in the chair's own frame,
        // even when the view is rotated) and rotation is left untouched;
        // velocities are zeroed so Play does not inherit the drag.
        target.z = g.lockZ;
        g.targetLocal = target;
        const d = target.clone().sub(g.prevTarget);
        const p = g.body.translation();
        g.body.setTranslation({ x: p.x + d.x, y: p.y + d.y, z: p.z }, true);
        g.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        g.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
        g.prevTarget = target.clone();

        // Remember where the chair has been parked, so a later regeneration
        // (editing a point, adding a slice...) puts it back here instead of
        // snapping it to the freshly-built body's centre of mass.
        //
        // ACCUMULATE the drag, rather than reading the offset back off the
        // live body (translation - centreOfMass). The body's translation also
        // carries whatever the SOLVER did to it - a chair that slid or toppled
        // under gravity - so reading it here baked that displacement into the
        // authored offset, and (now that the offset is saved with the design)
        // would persist a physics-derived pose. beginHandGrab snaps the body
        // back to the authored pose before a carry starts, so the two stay in
        // step; only the user's own movement can ever enter the offset.
        this.chairMoveOffset.x += d.x;
        this.chairMoveOffset.y += d.y;
    }

    /**
     * Snap the chair's compound body to its AUTHORED pose: the generated pose
     * plus the hand-tool offset, upright and at rest. Unlike
     * applyChairMoveOffset this runs even when the offset is zero, because its
     * job is to undo a SIMULATED displacement (a chair left tipped or slid by
     * a physics run) rather than to re-apply a parked one.
     *
     * Called when a paused hand grab takes hold of the chair: a carry drag
     * authors the placement, so it must start from the authored pose. Dragging
     * a fallen chair would otherwise move the body from where physics left it
     * while the offset accumulated from where the design says it lives - the
     * chair on screen and the value being saved would disagree.
     *
     * @returns {boolean} True when the body was actually displaced and moved
     */
    snapChairToAuthoredPose() {
        const compound = this.physicsWorld?.compoundBodies?.[0];
        if (!compound?.body) return false;

        const c = compound.centerOfMass;
        const off = this.chairMoveOffset;
        const authored = {
            x: c.x + off.x,
            y: c.y + off.y,
            z: c.z + off.z
        };

        const p = compound.body.translation();
        const r = compound.body.rotation();
        const displaced =
            Math.abs(p.x - authored.x) > 1e-4 ||
            Math.abs(p.y - authored.y) > 1e-4 ||
            Math.abs(p.z - authored.z) > 1e-4 ||
            Math.abs(1 - Math.abs(r.w)) > 1e-4; // any rotation off identity
        if (!displaced) return false;

        compound.body.setTranslation(authored, true);
        compound.body.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
        compound.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        compound.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
        this.physicsOverlayActive = true;
        this.updatePhysicsFrameDelta();
        return true;
    }

    /**
     * Park the chair's compound body at its generated pose plus the hand-tool
     * move offset, upright and at rest. Called after the body is (re)built -
     * which happens on every 3D regeneration - and after a physics reset:
     * without it, a chair the user dragged while physics was paused would jump
     * back to its pre-drag position the moment they edited a point.
     *
     * No-op (leaving every other pose untouched) when the chair has not been
     * hand-moved.
     */
    applyChairMoveOffset() {
        const off = this.chairMoveOffset;
        if (!off || off.lengthSq() === 0) return;

        const compound = this.physicsWorld?.compoundBodies?.[0];
        if (!compound?.body) return;

        const c = compound.centerOfMass;
        compound.body.setTranslation(
            { x: c.x + off.x, y: c.y + off.y, z: c.z + off.z }, true);
        // Upright: the parked pose is a translation of the generated one, so a
        // rotation left over from a previous simulation must not survive here
        compound.body.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
        compound.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        compound.body.setAngvel({ x: 0, y: 0, z: 0 }, true);

        // The chair now sits away from the pose the static sketch overlay was
        // built for, so the overlay has to track the body delta (as it does
        // while simulating)
        this.physicsOverlayActive = true;
        this.updatePhysicsFrameDelta();
    }

    /**
     * Hold newly built chair meshes at the parked pose.
     *
     * Regeneration rebuilds the meshes synchronously but the compound body -
     * which is what carries the parked position into syncMeshes - only comes
     * back in the async slice tail. Without this the chair renders at its
     * generated pose for those few frames: a visible flash back to where it
     * sat before the drag.
     *
     * Idempotent: each mesh is shifted at most once (unparkGeneratedMeshes
     * takes them back to the generated frame before the body is rebuilt).
     */
    parkGeneratedMeshes() {
        const off = this.chairMoveOffset;
        if (!off || off.lengthSq() === 0) return;
        for (const mesh of this.generatedMeshes) {
            if (mesh.userData.parkedByHand) continue;
            mesh.position.add(off);
            mesh.userData.parkedByHand = true;
        }
    }

    /**
     * Undo parkGeneratedMeshes: put the meshes back in their generated frame.
     * Called before the compound body is rebuilt, because the body's centre of
     * mass and collider transforms are derived from the mesh positions - built
     * from parked meshes, the parked offset would be baked into the body and
     * then applied a second time on top.
     */
    unparkGeneratedMeshes() {
        const off = this.chairMoveOffset;
        if (!off || off.lengthSq() === 0) return;
        for (const mesh of this.generatedMeshes) {
            if (!mesh.userData.parkedByHand) continue;
            mesh.position.sub(off);
            mesh.userData.parkedByHand = false;
        }
    }

    /**
     * Per-step spring impulse toward the hand-grab target (called from the
     * animate loop just before the physics step while playing).
     */
    updateHandGrab(dt) {
        const g = this.handGrab;
        if (!g || g.carry || !this.isPhysicsPlaying || !(dt > 0)) return;
        // Bodies get recreated (chair on regeneration, figure on
        // rebuild/scale) - drop stale grabs
        const stillAlive = g.isFigure
            ? this.ragdoll?.bodies?.includes(g.body)
            : this.physicsWorld.compoundBodies?.some(cb => cb.body === g.body);
        if (!stillAlive) {
            this.handGrab = null;
            return;
        }
        const body = g.body;
        const pos = body.translation();
        const rot = body.rotation();
        const q = new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w);
        const r = g.localOffset.clone().applyQuaternion(q);
        const grabPt = new THREE.Vector3(pos.x, pos.y, pos.z).add(r);

        const lin = body.linvel();
        const ang = body.angvel();
        const velAt = new THREE.Vector3(ang.x, ang.y, ang.z).cross(r)
            .add(new THREE.Vector3(lin.x, lin.y, lin.z));

        const mass = body.mass() || 1;
        const STIFFNESS = 60;  // spring accel per metre of error
        const DAMPING = 8;     // velocity damping at the grab point
        const force = g.targetLocal.clone().sub(grabPt).multiplyScalar(STIFFNESS)
            .sub(velAt.multiplyScalar(DAMPING))
            .multiplyScalar(mass);
        const maxForce = mass * 300;
        if (force.length() > maxForce) force.setLength(maxForce);

        const impulse = force.multiplyScalar(Math.min(dt, 1 / 30));
        body.applyImpulseAtPoint(
            { x: impulse.x, y: impulse.y, z: impulse.z },
            { x: grabPt.x, y: grabPt.y, z: grabPt.z },
            true
        );
    }

    // =====================================================================
    // MEASURE TOOL (ruler): click-drag on the drawing plane -> a persistent
    // measurement line with end ticks and a floating mm label
    // (sketch px = mm). One measurement at a time; cleared by Escape,
    // starting a new measurement, or switching tools.
    // =====================================================================

    /** (Re)draw the measurement overlay from this.measurement */
    renderMeasurement() {
        this._removeMeasureGroup();
        const m = this.measurement;
        if (!m) { this._hideMeasureLabel(); return; }

        const z = Math.max(...this.basePlanes.map(b => b.position.z)) + 0.05;
        const S = 0.01; // sketch px -> world
        const a = new THREE.Vector3(m.x0 * S, m.y0 * S, z);
        const b = new THREE.Vector3(m.x1 * S, m.y1 * S, z);

        const group = new THREE.Group();
        group.renderOrder = 1500;
        const mat = new THREE.LineBasicMaterial({
            color: 0x2b2b2b, depthTest: false, transparent: true, opacity: 0.95
        });
        const addLine = (p0, p1) => {
            const line = new THREE.Line(
                new THREE.BufferGeometry().setFromPoints([p0, p1]), mat);
            line.renderOrder = 1500;
            group.add(line);
        };
        addLine(a, b);

        // Small perpendicular end ticks (8px = 8mm long)
        const dir = b.clone().sub(a);
        if (dir.length() > 1e-6) {
            const perp = new THREE.Vector3(-dir.y, dir.x, 0).normalize().multiplyScalar(8 * S / 2);
            addLine(a.clone().add(perp), a.clone().sub(perp));
            addLine(b.clone().add(perp), b.clone().sub(perp));
        }

        this._measureGroup = group;
        this.addOverlay(group);
        this.updateMeasureLabel();
    }

    /** Remove the measurement (Escape / tool switch / plain click) */
    clearMeasurement() {
        this.measurement = null;
        this._removeMeasureGroup();
        this._hideMeasureLabel();
    }

    _removeMeasureGroup() {
        if (this._measureGroup) {
            this._measureGroup.parent?.remove(this._measureGroup);
            this._measureGroup.traverse(o => {
                o.geometry?.dispose?.();
                o.material?.dispose?.();
            });
            this._measureGroup = null;
        }
    }

    _hideMeasureLabel() {
        if (this._measureLabel) this._measureLabel.style.display = 'none';
    }

    // --- SCALE TOOL -------------------------------------------------------
    // Uniformly scales the WHOLE design (every stroke on every layer + the
    // chair depth) about the design centroid. Drag radially: away from centre
    // grows, toward centre shrinks. Geometry is scaled in the stroke store
    // from a pristine snapshot each update, so the factor is absolute (no
    // drift), then the design is rebuilt WITHOUT syncing from the live sketch
    // (which is scaled in lock-step) so the scale survives regeneration.

    /**
     * Begin a scale gesture. Returns false (a no-op) when there is no
     * geometry to scale or the pointer can't be projected onto the plane.
     * @param {PointerEvent} e
     */
    beginScaleGesture(e) {
        const pt = this.raycastSketchPoint(e);
        const bounds = this.strokeStore?.getSketchBounds?.();
        if (!pt || !bounds) return false;
        this.pushUndoSnapshot();
        this.scaleGesture = {
            cx: bounds.cx,
            cy: bounds.cy,
            // Floor the start radius so grabbing near the centroid can't blow
            // the factor up to infinity on the first move.
            startDist: Math.max(40, Math.hypot(pt.x - bounds.cx, pt.y - bounds.cy)),
            snapshot: this.strokeStore.captureState(),
            startChairWidth: this.chairWidth,
            activeLayer: this.activeSketchLayerIndex,
            // Baseline of the absolute-mm slice params so the ribs scale with
            // the design (spacing, cap length, crossbrace size) instead of
            // just multiplying in count at fixed spacing.
            sliceBaseline: (this.crossSliceSelections || []).map(s => ({
                spacing: s.spacing,
                capLength: s.capLength,
                crossbraceWidth: s.crossbraceWidth,
                crossbraceBottomOffset: s.crossbraceBottomOffset
            })),
            factor: 1,
            lastRegen: 0
        };
        this.canvas.style.cursor = 'nwse-resize';
        return true;
    }

    /** Apply the current scale factor (throttled heavy regen). */
    updateScaleGesture(e) {
        const g = this.scaleGesture;
        if (!g) return;
        const pt = this.raycastSketchPoint(e);
        if (!pt) return;
        const dist = Math.hypot(pt.x - g.cx, pt.y - g.cy);
        g.factor = Math.min(4, Math.max(0.25, dist / g.startDist));
        const now = (typeof performance !== 'undefined') ? performance.now() : 0;
        if (now - g.lastRegen < 120) return; // throttle the full rebuild
        g.lastRegen = now;
        this._applyScale(g, g.factor);
    }

    /** Commit the gesture at its final factor (or discard a no-op). */
    endScaleGesture() {
        const g = this.scaleGesture;
        if (!g) return;
        this.scaleGesture = null;
        this.canvas.style.cursor = 'crosshair';
        if (Math.abs(g.factor - 1) < 0.002) {
            // No real change (a click, or barely moved): restore exactly and
            // drop the snapshot we pushed so it isn't a phantom undo step.
            this.strokeStore.restoreState(g.snapshot);
            this.discardLastUndoSnapshot?.();
            this.resolveLayerStates();
            this.loadLayerIntoSketch(g.activeLayer);
            this.regenerateAll({ syncSketch: false });
            return;
        }
        this._applyScale(g, g.factor);
        this.autosaveDesign?.();
        this.uiManager?.panels?.layers?.update?.();
    }

    /**
     * Rebuild the design at absolute scale factor f from a gesture's pristine
     * snapshot: scale strokes + chair depth, materialize the scaled masters
     * into every plane, refresh the live sketch view, and regenerate WITHOUT
     * a sync-from-live (which would clobber the scale).
     * @param {Object} g - the scale gesture (snapshot + centroid + baseline)
     * @param {number} f
     */
    _applyScale(g, f) {
        if (!g) return;
        this.strokeStore.restoreState(g.snapshot);
        this.strokeStore.scaleAbout(g.cx, g.cy, f);
        this.chairWidth = g.startChairWidth * f;
        this.basePlaneSpacing = this.chairWidth / Math.max(1, this.numBasePlanes - 1);
        this.updateBasePlanePositions();
        // Scale the cross-slice params (absolute mm) so ribs, caps and
        // crossbraces scale WITH the design rather than staying fixed while
        // the chair grows around them.
        if (g.sliceBaseline) {
            const sels = this.crossSliceSelections || [];
            g.sliceBaseline.forEach((base, i) => {
                const s = sels[i];
                if (!s) return;
                if (Number.isFinite(base.spacing)) s.spacing = base.spacing * f;
                if (Number.isFinite(base.capLength)) s.capLength = base.capLength * f;
                if (Number.isFinite(base.crossbraceWidth)) s.crossbraceWidth = base.crossbraceWidth * f;
                if (Number.isFinite(base.crossbraceBottomOffset)) s.crossbraceBottomOffset = base.crossbraceBottomOffset * f;
            });
        }
        this.resolveLayerStates();
        this.loadLayerIntoSketch(g.activeLayer);
        this.regenerateAll({ syncSketch: false });
    }

    /**
     * Reproject the floating mm label to the measurement midpoint (called
     * every frame from the animate loop; cheap no-op when no measurement).
     */
    updateMeasureLabel() {
        const m = this.measurement;
        if (!m) { this._hideMeasureLabel(); return; }

        if (!this._measureLabel) {
            const el = document.createElement('div');
            el.style.cssText = `
                position: absolute; display: none; z-index: 30;
                background: rgba(30, 30, 30, 0.92); border: 1px solid #999;
                border-radius: 8px; padding: 4px 10px; color: #fff;
                font-size: 12px; pointer-events: none; white-space: nowrap;
                transform: translate(-50%, -140%);`;
            document.getElementById('canvas-container')?.appendChild(el);
            this._measureLabel = el;
        }

        const dist = Math.hypot(m.x1 - m.x0, m.y1 - m.y0); // sketch px = mm
        this._measureLabel.textContent = `${dist.toFixed(1)} mm`;

        const z = Math.max(...this.basePlanes.map(b => b.position.z)) + 0.05;
        const pivot = this.sceneRotation?.getPivotGroup();
        if (pivot) pivot.updateMatrixWorld(true);
        const mid = new THREE.Vector3(
            (m.x0 + m.x1) / 2 * 0.01, (m.y0 + m.y1) / 2 * 0.01, z);
        mid.applyMatrix4(composeFrameMatrix(pivot ? pivot.matrixWorld : null, this.physicsFrameDelta));
        mid.project(this.cameraController.camera);
        const rect = this.canvas.getBoundingClientRect();
        this._measureLabel.style.left = `${(mid.x + 1) / 2 * rect.width}px`;
        this._measureLabel.style.top = `${(1 - mid.y) / 2 * rect.height}px`;
        this._measureLabel.style.display = 'block';
    }

    /**
     * Setup drawing interaction for unified 3D view (Phase 3.5)
     * Handles left mouse button for drawing on 3D planes
     */
    setupDrawingInteraction() {
        if (!this.cameraController || !this.planeIntersector) return;

        // Mouse state
        let isLeftDragging = false;

        // Mouse down - start drawing
        this.canvas.addEventListener('pointerdown', (e) => {
            // Camera panel mode active: left-drag controls the camera, not drawing
            if (this.activeCameraMode) return;

            // Keep the whole gesture's pointer stream on the canvas. Legs
            // are dragged DOWN toward the floor, so the release often lands
            // on the bottom toolbar/settings card (or outside the window) -
            // without capture the canvas never receives pointerup, the
            // leg/stroke silently never commits and isDrawing is left
            // dangling ("leg disappears on release").
            try { this.canvas.setPointerCapture(e.pointerId); } catch (_) { /* synthetic pointers */ }

            // HAND TOOL: left-click grabs the chair OR the figure during
            // physics play. Only steals the click when it actually hits
            // something, so camera controls stay unaffected.
            if (e.button === 0 && this.sketchPlane.tool === 'hand') {
                this.beginHandGrab(e);
                e.preventDefault();
                return;
            }

            // SELECT TOOL: can grab the FIGURE too - spring-drag while
            // physics plays, kinematic carry while paused (silent - a miss
            // falls through to normal sketch selection)
            if (e.button === 0 && this.sketchPlane.tool === 'select' &&
                this.beginHandGrab(e, { figureOnly: true, silent: true })) {
                e.preventDefault();
                return;
            }

            // A left-click that reaches here did NOT start on the figure, so
            // it clears any figure selection (the pill dismisses). Figure
            // grabs return above, keeping their selection through the gesture.
            if (e.button === 0 && this._figureSelected) this.deselectFigure();

            // MEASURE TOOL: click-drag draws a measurement on the drawing
            // plane (persists until re-measured / Escape / tool switch)
            if (e.button === 0 && this.sketchPlane.tool === 'measure') {
                const pt = this.raycastSketchPoint(e);
                if (pt) {
                    this.measurement = { x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y, dragging: true };
                    this.renderMeasurement();
                }
                e.preventDefault();
                return;
            }

            // SCALE TOOL: left-drag uniformly scales the whole design about
            // its centroid. Consumes the gesture even if there's nothing to
            // scale (so it never falls through to drawing).
            if (e.button === 0 && this.sketchPlane.tool === 'scale') {
                this.beginScaleGesture(e);
                e.preventDefault();
                return;
            }

            // Interactive slice placement armed (Slices panel): clicks place
            // slices instead of drawing (mirrors the activeCameraMode gate).
            // In 'edit' mode clicks grab existing ribs instead - and never
            // fall through to the sketch tools, even on a miss.
            if (e.button === 0 && this.sliceTool) {
                const pt = this.raycastSketchPoint(e);
                if (pt) {
                    if (this.sliceTool.type === 'edit') {
                        const grabRadius = 12 / (0.01 * this.cameraController.zoom);
                        const hit = this.findRibAt(pt.x, pt.y, Math.max(10, grabRadius));
                        if (hit && this.beginSliceDrag(hit, pt.x, pt.y)) {
                            this.clearSlicePreview();
                            // Select the grabbed rib's group in the panel so
                            // the option toggles reflect its state
                            this.uiManager?.panels?.slices?.selectSliceGroup?.(hit.selection);
                        }
                    } else {
                        this.handleSliceToolPointerDown(pt.x, pt.y);
                    }
                }
                e.preventDefault();
                return;
            }

            // Click-drag editing of an existing rib: only steal the click
            // from the sketch when the select tool is active AND the click
            // actually lands on a rib chord (~10 sketch px at default zoom)
            if (e.button === 0 && this.sketchPlane.tool === 'select') {
                const pt = this.raycastSketchPoint(e);
                if (pt) {
                    const grabRadius = 12 / (0.01 * this.cameraController.zoom);
                    // Sketch control points take priority: grabbing a centre
                    // point must never fall through to a rib-spacing drag.
                    // Probe EVERY selected plane's ray intersection - under
                    // scene rotation each plane yields different sketch
                    // coords for the same click (parallax), and the point
                    // being grabbed may live on any of them.
                    const probe = PlaneIntersector.getSelectedPlaneIntersections(
                        e.clientX, e.clientY,
                        this.basePlanes, this.selectedBasePlaneIndices,
                        this.cameraController.camera, this.canvas,
                        this.sceneRotation?.getPivotGroup(), this.physicsFrameDelta
                    );
                    const pointNearby = probe.some(h =>
                        this.sketchPlane.hasGrabbablePointAt?.(
                            h.intersection.x * 100, h.intersection.y * 100,
                            Math.max(10, grabRadius)));
                    const hit = pointNearby ? null : this.findRibAt(pt.x, pt.y, Math.max(10, grabRadius));
                    if (hit && this.beginSliceDrag(hit, pt.x, pt.y)) {
                        e.preventDefault();
                        return;
                    }
                }
            }

            // Handle left-click for all tools. Right-click goes to the sketch
            // only where it has meaning: select tool, or finishing an
            // in-progress offset/pen path (otherwise right-drag just rotates
            // the scene via the camera controls).
            const shouldHandleLeftClick = e.button === 0;
            const shouldHandleRightClick = e.button === 2 && (
                this.sketchPlane.tool === 'select' ||
                (this.sketchPlane.tool === 'offset' && !!this.sketchPlane.currentSpline) ||
                (this.sketchPlane.tool === 'pen' && !!this.sketchPlane.currentPath)
            );

            if (shouldHandleLeftClick || shouldHandleRightClick) {
                const isRightClick = e.button === 2;

                // Only set dragging flag for left-click
                if (e.button === 0) {
                    isLeftDragging = true;
                }

                // Ray cast to selected planes
                const intersections = PlaneIntersector.getSelectedPlaneIntersections(
                    e.clientX,
                    e.clientY,
                    this.basePlanes,
                    this.selectedBasePlaneIndices,
                    this.cameraController.camera,
                    this.canvas,
                    this.sceneRotation?.getPivotGroup(),
                    this.physicsFrameDelta
                );

                // If we hit a plane, process the click
                if (intersections.length > 0) {
                    // Hit radius: 12 screen pixels converted to sketch pixels
                    // (1 sketch px = 0.01 world units, 1 screen px = 1/zoom world units)
                    const hitRadius = 12 / (0.01 * this.cameraController.zoom);

                    const isEditTool = this.sketchPlane.tool === 'select' || this.sketchPlane.tool === 'bezier';
                    if (isEditTool && !isRightClick) {
                        // Snapshot BEFORE the grab: the bezier tool can
                        // mutate (create handles) on pointer-down
                        this.pushUndoSnapshot();
                        // When the scene is rotated, each selected plane's ray
                        // intersection gives DIFFERENT sketch coordinates
                        // (parallax between the Z levels). The clicked
                        // point/handle/line/body sits on one specific plane,
                        // so probe EVERY plane's coordinates with a
                        // NON-MUTATING hit test and dispatch the real
                        // pointer-down once, with the first plane that hits.
                        // (Previously the full pointer-down ran per plane
                        // until a POINT was grabbed: each successive call
                        // re-ran the whole select logic - clearing the
                        // previous plane's shape selection, missing
                        // body/centre-line hits entirely and overwriting
                        // them with a marquee - so with the model rotated,
                        // selection only ever worked on the last plane in
                        // the intersection order.)
                        this.activeDragPlaneIndex = null;
                        // Rank across planes by HIT TYPE first (handle >
                        // point > line > body), plane order second - a
                        // precise point on a far plane beats a big body
                        // hit on a near plane
                        let chosen = null;
                        let chosenClass = Infinity;
                        for (const hit of intersections) {
                            const cls = this.sketchPlane.hitTestEditTool(
                                hit.intersection.x * 100,
                                hit.intersection.y * 100,
                                hitRadius
                            );
                            if (cls > 0 && cls < chosenClass) {
                                chosen = hit;
                                chosenClass = cls;
                                if (cls === 1) break; // can't do better
                            }
                        }
                        // Miss on every plane: dispatch with the front
                        // plane's coords (starts the marquee / empty-click
                        // clear exactly as before)
                        const target = chosen || intersections[0];
                        this.sketchPlane.handlePointerDown3D(
                            target.intersection.x * 100,
                            target.intersection.y * 100,
                            e.shiftKey,
                            isRightClick,
                            hitRadius
                        );
                        if (chosen) {
                            // Keep the whole drag (point, handle OR whole-
                            // stroke centre-line drag) in the frame of the
                            // plane that was actually hit
                            this.activeDragPlaneIndex = chosen.index;
                        }
                        // Keep the snapshot for point/handle drags AND
                        // whole-stroke centre-line drags (draggedSpline);
                        // pure selection clicks are not undoable
                        if (!this.sketchPlane.draggedPoint && !this.sketchPlane.draggedSpline) {
                            this.discardLastUndoSnapshot();
                        }
                    } else {
                        // Drawing tools: use the first intersection
                        const { intersection } = intersections[0];
                        const worldX = intersection.x * 100; // Convert to canvas pixels
                        const worldY = intersection.y * 100;
                        console.log('[Application] Calling handlePointerDown3D, button:', e.button, 'isRightClick:', isRightClick);
                        this.sketchPlane.handlePointerDown3D(worldX, worldY, e.shiftKey, isRightClick, hitRadius);
                    }

                    // Create/update preview line for real-time feedback (only for left-click/drawing)
                    if (!isRightClick) {
                        this.createDrawingPreview(intersections[0].index);
                        // Update preview immediately to show the red line with current points
                        this.updateDrawingPreview(intersections[0].index);
                    } else if (!this.sketchPlane.currentSpline && !this.sketchPlane.currentPath) {
                        // Right-click finished (or discarded) a click-based path
                        this.removeDrawingPreview();
                    }

                    // Render to show changes
                    this.updateSketchRendering();

                    // Prevent default only for handled events
                    e.preventDefault();
                }
                // If we didn't hit a plane and it's left-click, prevent default anyway
                // But for right-click (scene rotation), don't prevent if no plane hit
                else if (e.button === 0) {
                    e.preventDefault();
                }
            }
        });

        // Mouse move - continue drawing or update preview
        this.canvas.addEventListener('pointermove', (e) => {
            // Hand grab in progress: move the spring target on the
            // Scale gesture in progress: uniformly resize the design
            if (this.scaleGesture) {
                this.updateScaleGesture(e);
                e.preventDefault();
                return;
            }

            // camera-parallel drag plane through the grab point
            if (this.handGrab) {
                this.updateHandGrabTarget(e);
                e.preventDefault();
                return;
            }

            // Measurement drag in progress: track the far endpoint
            if (this.measurement?.dragging) {
                const pt = this.raycastSketchPoint(e);
                if (pt) {
                    this.measurement.x1 = pt.x;
                    this.measurement.y1 = pt.y;
                    this.renderMeasurement();
                }
                e.preventDefault();
                return;
            }

            // Armed slice tool: live placement/density preview
            if (this.sliceTool) {
                this.updateSlicePreview(e);
            }

            // Rib drag in progress: move the slice group along its guide path
            if (this.sliceDrag) {
                const pt = this.raycastSketchPoint(e);
                if (pt) {
                    this.updateSliceDrag(pt.x, pt.y);
                }
                e.preventDefault();
                return;
            }

            // Handle both drag-based drawing (brush) and click-based preview updates (pen/offset)
            const shouldHandleMove = isLeftDragging && this.sketchPlane.isDrawing;
            const isOffsetToolWithPoints = this.sketchPlane.tool === 'offset' &&
                                          this.sketchPlane.currentSpline &&
                                          this.sketchPlane.currentSpline.centrePath?.points?.length > 0;

            if (shouldHandleMove || isOffsetToolWithPoints) {
                // Ray cast to selected planes
                const intersections = PlaneIntersector.getSelectedPlaneIntersections(
                    e.clientX,
                    e.clientY,
                    this.basePlanes,
                    this.selectedBasePlaneIndices,
                    this.cameraController.camera,
                    this.canvas,
                    this.sceneRotation?.getPivotGroup(),
                    this.physicsFrameDelta
                );

                if (intersections.length > 0) {
                    // Select-tool drags must stay in the frame of the plane
                    // that GRABBED the point - under scene rotation the
                    // planes' ray intersections differ (parallax)
                    let hit = intersections[0];
                    const editToolDrag = this.sketchPlane.tool === 'select' || this.sketchPlane.tool === 'bezier';
                    if (editToolDrag && this.activeDragPlaneIndex != null) {
                        hit = intersections.find(h => h.index === this.activeDragPlaneIndex) || hit;
                    }

                    const worldX = hit.intersection.x * 100;
                    const worldY = hit.intersection.y * 100;

                    // For offset tool, update the preview point position
                    if (isOffsetToolWithPoints) {
                        this.sketchPlane.handlePointerMove3D(worldX, worldY);
                    } else if (shouldHandleMove) {
                        // For brush/leg/select tools, normal drag behavior
                        this.sketchPlane.handlePointerMove3D(worldX, worldY);
                    }

                    // Update real-time preview line
                    this.updateDrawingPreview(hit.index);
                }

                if (shouldHandleMove) {
                    e.preventDefault();
                }
            }
        });

        // Mouse up - finish drawing
        this.canvas.addEventListener('pointerup', (e) => {
            // Commit an in-progress scale gesture
            if (e.button === 0 && this.scaleGesture) {
                this.endScaleGesture();
                return;
            }

            // Release the hand-tool grab: the chair is freed to physics
            if (e.button === 0 && this.handGrab) {
                // A paused chair carry re-parked the chair, which is authored
                // design state (settings.chairPlacement) - persist it. Nothing
                // else does: autosave otherwise only runs on a sketch edit, so
                // a move on its own would be lost on reload. Spring drags and
                // figure grabs author nothing.
                const g = this.handGrab;
                const parked = g.carry && !g.isFigure;
                this.handGrab = null;
                // A figure grab that barely moved is a CLICK: select the
                // figure and float its control pill. A real drag carried it.
                if (g.isFigure) {
                    const moved = Math.hypot(e.clientX - g.downX, e.clientY - g.downY);
                    if (moved < 5) this.selectFigure();
                    else this.updateFigureWidget();
                } else {
                    console.log('[HAND] Released the chair');
                }
                if (parked) this.autosaveDesign();
                return;
            }

            // Finish a measurement drag (a plain click clears instead)
            if (e.button === 0 && this.measurement?.dragging) {
                this.measurement.dragging = false;
                const len = Math.hypot(this.measurement.x1 - this.measurement.x0,
                    this.measurement.y1 - this.measurement.y0);
                if (len < 2) this.clearMeasurement();
                return;
            }

            // Finish an in-progress rib drag (always regenerates once more)
            if (e.button === 0 && this.sliceDrag) {
                this.endSliceDrag();
                return;
            }

            if (e.button === 0 && isLeftDragging) {
                isLeftDragging = false;

                if (this.sketchPlane.isDrawing) {
                    this.sketchPlane.handlePointerUp3D();

                    // Remove preview line
                    this.removeDrawingPreview();

                    // Force final update of 3D rendering
                    this.updateSketchRendering();

                    // Force scene update
                    this.sceneManager.render();
                }
            }
        });

        console.log('Drawing interaction initialized (Phase 3.5)');
        console.log('- Left click+drag: Draw on planes');
        console.log('- B/N/S keys: Switch tools (brush/pen/select)');
        console.log('- C key: Clear all');
    }

    /**
     * Update 3D sketch rendering for all selected planes (Phase 3.5)
     */
    /**
     * Floating "stroke width" widget: shown when control points of an
     * OFFSET path are selected; edits the per-point offset radius
     * (spline.centreOffset) for just the selected points.
     */
    /**
     * Dim panel meshes belonging to UNSELECTED layers so the active layers
     * stand out (unselected at higher transparency). Rib meshes and other
     * non-layer meshes stay fully opaque.
     */
    updateLayerMeshOpacity() {
        // Ribs/slices have no layer of their own: they render normally when
        // ALL layers are selected (or none focused), and semi-transparent
        // when the user is focused on a subset of layers
        const selectableCount = this.basePlanes.filter(p => !p.guide).length;
        const allSelected = this.selectedBasePlaneIndices.size >= selectableCount;

        for (const mesh of this.generatedMeshes) {
            if (!mesh.material) continue;
            const idx = mesh.userData?.basePlaneIndex;
            const isRib = idx === undefined;
            const full = isRib ? allSelected : this.selectedBasePlaneIndices.has(idx);
            const opacity = full ? 1.0 : 0.3;
            if (mesh.material.opacity !== opacity) {
                mesh.material.transparent = true;
                mesh.material.opacity = opacity;
                mesh.material.depthWrite = full; // translucent parts don't occlude
                mesh.material.needsUpdate = true;
            }
        }
    }

    /**
     * Select the ergonomic figure (from a click): float its control pill.
     * No-op when the figure is hidden.
     */
    selectFigure() {
        if (!this.ragdoll?.visible) return;
        this._figureSelected = true;
        this.updateFigureWidget();
    }

    /** Clear the figure selection and hide its pill. */
    deselectFigure() {
        this._figureSelected = false;
        if (this._figureWidget) this._figureWidget.box.style.display = 'none';
    }

    /**
     * Lazily build the floating figure pill: a height/age readout over a
     * row of sit/stand + size controls, anchored above the figure's head.
     * The controls proxy the same Application methods the side panel uses.
     */
    ensureFigureWidget() {
        if (this._figureWidget) return this._figureWidget;

        const box = document.createElement('div');
        box.style.cssText = `
            position: absolute; display: none; z-index: 30;
            flex-direction: column; gap: 6px; align-items: stretch;
            transform: translate(-50%, -100%);
            background: rgba(30, 30, 30, 0.92); border: 1px solid #00E5FF;
            border-radius: 10px; padding: 6px 8px; color: #fff; font-size: 12px;`;
        // Clicks on the pill must not fall through to the canvas (which would
        // deselect). The pill is a sibling of the canvas, so also stop here.
        box.addEventListener('pointerdown', (e) => e.stopPropagation());

        const readout = document.createElement('div');
        readout.style.cssText = 'text-align: center; color: #00E5FF; font-weight: 600; white-space: nowrap;';
        box.appendChild(readout);

        const row = document.createElement('div');
        row.style.cssText = 'display: flex; gap: 4px; justify-content: center;';
        const mkBtn = (label, title, onClick) => {
            const b = document.createElement('button');
            b.textContent = label;
            b.title = title;
            b.style.cssText = `
                min-width: 34px; height: 26px; padding: 0 8px; cursor: pointer;
                background: #222; color: #fff; border: 1px solid #555;
                border-radius: 6px; font-size: 13px; line-height: 1;
                display: flex; align-items: center; justify-content: center;`;
            b.addEventListener('mouseenter', () => { b.style.background = '#333'; });
            b.addEventListener('mouseleave', () => { b.style.background = '#222'; });
            b.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
            return b;
        };

        const afterChange = () => {
            this.updateFigureWidget();
            // Keep the side panel's Figure status/button in step with the pill
            this.uiManager?.panels?.physics?.syncFigureState?.();
        };
        const sitBtn = mkBtn('Sit', 'Sit / stand the figure', () => {
            this.toggleFigurePose();
            afterChange();
        });
        const smaller = mkBtn('−', 'Smaller', () => { this.scaleRagdoll(0.9); afterChange(); });
        const bigger = mkBtn('+', 'Bigger', () => { this.scaleRagdoll(1.1); afterChange(); });
        row.append(sitBtn, smaller, bigger);
        box.appendChild(row);

        document.getElementById('canvas-container')?.appendChild(box);
        this._figureWidget = { box, readout, sitBtn };
        return this._figureWidget;
    }

    /**
     * Refresh the figure pill: readout text, sit/stand label, and screen
     * anchor above the head. Called every frame while selected (cheap
     * no-op otherwise) and after any size/pose change. Also the live
     * "height + age above the character" label the size buttons show.
     */
    updateFigureWidget() {
        const doll = this.ragdoll;
        if (!this._figureSelected || !doll?.visible) {
            if (this._figureWidget) this._figureWidget.box.style.display = 'none';
            return;
        }
        const w = this.ensureFigureWidget();
        const metres = (doll.getStandingHeight() / 10).toFixed(2);
        w.readout.textContent = `${metres} m · ${doll.getAgeDescription()}`;
        w.sitBtn.textContent = doll.currentPose === 'seated' ? 'Stand' : 'Sit';

        // Anchor above the crown. Head mesh world position handles whatever
        // frame the figure lives in (getWorldPosition walks the pivot).
        const head = doll.meshes?.[Ragdoll.BodyPart.HEAD];
        if (!head) { w.box.style.display = 'none'; return; }
        const wp = new THREE.Vector3();
        head.getWorldPosition(wp);
        wp.y += 1.2; // clear the top of the head
        wp.project(this.cameraController.camera);
        if (wp.z > 1) { w.box.style.display = 'none'; return; } // behind camera
        const rect = this.canvas.getBoundingClientRect();
        w.box.style.left = `${(wp.x + 1) / 2 * rect.width}px`;
        w.box.style.top = `${(1 - wp.y) / 2 * rect.height - 8}px`;
        w.box.style.display = 'flex';
    }

    updateOffsetWidthWidget() {
        const spline = this.sketchPlane?.selectedSpline;
        const nodes = spline?.selectedNodes || [];
        // Offset paths AND legs both carry per-point radii (centreOffset):
        // legs taper top->foot, and editing a leg point's width reshapes
        // both the leg outline and its rib extension on regeneration
        const isOffsetPath = spline &&
            (spline.type === 4 /* SketchShape.OFFSET_SPLINE */ ||
             spline.type === SketchSpline.TYPE_LEG);

        if (!this._offsetWidthWidget) {
            const box = document.createElement('div');
            box.style.cssText = `
                position: absolute; display: none; z-index: 30;
                background: rgba(30, 30, 30, 0.92); border: 1px solid #00E5FF;
                border-radius: 8px; padding: 6px 10px; color: #fff;
                font-size: 12px; display: none; align-items: center; gap: 6px;`;
            const label = document.createElement('span');
            label.textContent = 'Width';
            label.style.color = '#00E5FF';
            const input = document.createElement('input');
            input.type = 'number';
            input.min = '4'; input.max = '400'; input.step = '2';
            input.style.cssText = 'width: 58px; background: #222; color: #fff; border: 1px solid #555; border-radius: 4px; padding: 2px 4px;';
            input.addEventListener('change', () => {
                const w = parseFloat(input.value);
                if (!Number.isFinite(w) || w <= 0) return;
                const sp = this.sketchPlane?.selectedSpline;
                if (!sp) return;
                for (const node of sp.selectedNodes || []) {
                    const idx = sp.centrePath.points.indexOf(node);
                    if (idx >= 0) sp.centreOffset.set(idx, w / 2);
                }
                sp.build();
                this.sketchPlane.rebuildShapeLists();
                this.sketchPlane.onShapeChange();
            });
            input.addEventListener('pointerdown', (e) => e.stopPropagation());
            box.appendChild(label);
            box.appendChild(input);
            const unit = document.createElement('span');
            unit.textContent = 'mm';
            unit.style.color = '#888';
            box.appendChild(unit);

            // Scrub: click-drag up/down anywhere on the widget to change
            // the width live (up = wider), full regeneration on release
            box.style.cursor = 'ns-resize';
            const applyWidth = (w, commit) => {
                const sp = this.sketchPlane?.selectedSpline;
                if (!sp) return;
                for (const node of sp.selectedNodes || []) {
                    const idx = sp.centrePath.points.indexOf(node);
                    if (idx >= 0) sp.centreOffset.set(idx, w / 2);
                }
                if (commit) {
                    sp.build();
                    this.sketchPlane.rebuildShapeLists();
                    this.sketchPlane.onShapeChange();
                } else {
                    sp.offset();
                    sp.buildPath();
                    this.sketchPlane.rebuildShapeLists();
                    this.sketchPlane.onDragPreview?.();
                }
            };
            box.addEventListener('pointerdown', (e) => {
                if (e.target === input) return;
                e.stopPropagation();
                e.preventDefault();
                const startY = e.clientY;
                const startW = parseFloat(input.value) || 24;
                try { box.setPointerCapture(e.pointerId); } catch (_) { /* synthetic pointers */ }
                const onMove = (ev) => {
                    const w = Math.min(400, Math.max(4, Math.round(startW + (startY - ev.clientY))));
                    input.value = w;
                    applyWidth(w, false);
                };
                const onUp = (ev) => {
                    window.removeEventListener('pointermove', onMove);
                    window.removeEventListener('pointerup', onUp);
                    const w = Math.min(400, Math.max(4, Math.round(startW + (startY - ev.clientY))));
                    input.value = w;
                    applyWidth(w, true);
                };
                window.addEventListener('pointermove', onMove);
                window.addEventListener('pointerup', onUp);
            });
            document.getElementById('canvas-container')?.appendChild(box);
            this._offsetWidthWidget = { box, input };
        }

        const widget = this._offsetWidthWidget;
        if (!isOffsetPath || nodes.length === 0) {
            widget.box.style.display = 'none';
            return;
        }

        // Current width of the first selected node (per-point radius or
        // the spline default)
        const idx = spline.centrePath.points.indexOf(nodes[0]);
        const r = (idx >= 0 && spline.centreOffset.has(idx)) ? spline.centreOffset.get(idx) : spline.offsetSize;
        if (document.activeElement !== widget.input) widget.input.value = Math.round(r * 2);

        // Position beside the selected point (screen space)
        const cam = this.cameraController.camera;
        const rect = this.canvas.getBoundingClientRect();
        const pivot = this.sceneRotation?.getPivotGroup();
        if (pivot) pivot.updateMatrixWorld(true);
        const wp = new THREE.Vector3(nodes[0].x * 0.01, nodes[0].y * 0.01, 0);
        wp.applyMatrix4(composeFrameMatrix(pivot ? pivot.matrixWorld : null, this.physicsFrameDelta));
        wp.project(cam);
        widget.box.style.left = `${(wp.x + 1) / 2 * rect.width + 18}px`;
        widget.box.style.top = `${(1 - wp.y) / 2 * rect.height - 16}px`;
        widget.box.style.display = 'flex';
    }

    /**
     * On-model CHAIR-CURVE scrub pill: shown when a centre point of a
     * LINKED stroke is selected while the chair curve is active. Scrubbing
     * up/down edits the curve's LENGTH PROFILE at that point's arc
     * position s (normalized point index - the same s resolution uses),
     * locally growing/shrinking the bulge. Inserts/updates a profile
     * control point at that s (nearest within tolerance, max 5 points).
     * Live rebuild while scrubbing; one undo snapshot per gesture.
     */
    updateCurveScrubWidget() {
        const sp = this.sketchPlane;
        const spline = sp?.selectedSpline;
        const nodes = spline?.selectedNodes || [];
        const store = this.strokeStore;
        const show = !!(spline && nodes.length > 0 && spline.strokeId &&
            spline.type !== SketchSpline.TYPE_LEG &&
            store?.masters?.has(spline.strokeId) && store.isChairCurveActive());

        if (!this._curveScrubWidget) {
            const box = document.createElement('div');
            box.style.cssText = `
                position: absolute; display: none; z-index: 30;
                background: rgba(30, 30, 30, 0.92); border: 1px solid #FF9800;
                border-radius: 8px; padding: 6px 10px; color: #fff;
                font-size: 12px; align-items: center; gap: 6px;
                cursor: ns-resize; user-select: none;`;
            const label = document.createElement('span');
            label.textContent = 'Curve';
            label.style.color = '#FF9800';
            const value = document.createElement('span');
            value.style.cssText = 'min-width: 34px; text-align: right;';
            box.appendChild(label);
            box.appendChild(value);
            box.title = 'Drag up/down: more/less chair-curve bulge at this point';

            box.addEventListener('pointerdown', (e) => {
                e.stopPropagation();
                e.preventDefault();
                const widget = this._curveScrubWidget;
                if (widget.s == null) return;
                this.pushUndoSnapshot();
                const { evaluateLengthProfile } = widget.helpers;
                const startY = e.clientY;
                // Capture the gesture's arc position ONCE: mid-gesture
                // rebuilds refresh the widget and can transiently null
                // widget.s, which made later scrub updates land at s=0
                const gestureS = widget.s;
                const startW = evaluateLengthProfile(
                    this.strokeStore.getChairCurve().lengthProfile, gestureS);
                const strokeId = widget.strokeId;
                const nodeIndex = widget.nodeIndex;
                try { box.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }

                // Rebuild + reload live, then restore the point-level
                // selection so the widget and drag survive the reload
                const commit = () => {
                    this.commitStoreMutation();
                    const again = [...this.sketchPlane.splines, ...this.sketchPlane.paths]
                        .find(s => s.strokeId === strokeId);
                    if (again && again.centrePath) {
                        this.sketchPlane.selectShape(again);
                        const pt = again.centrePath.points[nodeIndex];
                        if (pt) again.selectNode(pt);
                    }
                };
                let lastRegen = 0;
                const onMove = (ev) => {
                    const w = Math.min(1.5, Math.max(0,
                        startW + (startY - ev.clientY) * 0.01));
                    this.strokeStore.nudgeLengthProfilePoint(gestureS, w);
                    widget.value.textContent = `${Math.round(w * 100)}%`;
                    this.uiManager?.panels?.layers?.lengthProfileEditor?.draw?.();
                    const now = performance.now();
                    if (now - lastRegen >= 150) {
                        lastRegen = now;
                        commit();
                    }
                };
                const onUp = () => {
                    window.removeEventListener('pointermove', onMove);
                    window.removeEventListener('pointerup', onUp);
                    commit(); // final, unthrottled
                };
                window.addEventListener('pointermove', onMove);
                window.addEventListener('pointerup', onUp);
            });

            document.getElementById('canvas-container')?.appendChild(box);
            this._curveScrubWidget = { box, value, s: null, strokeId: null, nodeIndex: -1, helpers: {} };
            // Lazy import-free helper binding (module already imported)
            import('./StrokeStore.js').then(m => {
                this._curveScrubWidget.helpers.evaluateLengthProfile = m.evaluateLengthProfile;
            });
        }

        const widget = this._curveScrubWidget;
        if (!show || !widget.helpers.evaluateLengthProfile) {
            widget.box.style.display = 'none';
            widget.s = null;
            return;
        }

        const pts = spline.centrePath.points;
        const idx = pts.indexOf(nodes[0]);
        if (idx < 0) {
            widget.box.style.display = 'none';
            widget.s = null;
            return;
        }
        widget.s = pts.length > 1 ? idx / (pts.length - 1) : 0;
        widget.strokeId = spline.strokeId;
        widget.nodeIndex = idx;
        const w = widget.helpers.evaluateLengthProfile(
            this.strokeStore.getChairCurve().lengthProfile, widget.s);
        widget.value.textContent = `${Math.round(w * 100)}%`;

        // Position beside the selected point, stacked BELOW the width
        // widget slot so both pills can show together on offset paths
        const cam = this.cameraController.camera;
        const rect = this.canvas.getBoundingClientRect();
        const pivot = this.sceneRotation?.getPivotGroup();
        if (pivot) pivot.updateMatrixWorld(true);
        const wp = new THREE.Vector3(nodes[0].x * 0.01, nodes[0].y * 0.01, 0);
        wp.applyMatrix4(composeFrameMatrix(pivot ? pivot.matrixWorld : null, this.physicsFrameDelta));
        wp.project(cam);
        widget.box.style.left = `${(wp.x + 1) / 2 * rect.width + 18}px`;
        widget.box.style.top = `${(1 - wp.y) / 2 * rect.height + 18}px`;
        widget.box.style.display = 'flex';
    }

    /**
     * Is this base plane an OUTER layer (first or last)?
     * Legs live ONLY on the outer layers - matching the Java original and
     * the leg tool's auto-selection of layers 0 and N-1. This predicate is
     * stable across layer-selection changes, unlike "currently selected".
     */
    isOuterBasePlane(planeIndex) {
        return planeIndex === 0 || planeIndex === this.basePlanes.length - 1;
    }

    /**
     * Clone a sketch state for storage on plane `planeIndex`, enforcing the
     * legs-only-on-outer-layers rule: inner planes get the sketch with
     * TYPE_LEG splines and legShapes stripped, so later edits/rebuilds can
     * never resurrect leg geometry on them.
     * @param {Object} state - Sketch state from sketchPlane.getSketchState()
     * @param {number} planeIndex - Target base plane index
     */
    cloneSketchStateForPlane(state, planeIndex) {
        const keepLegs = this.isOuterBasePlane(planeIndex);
        return {
            paths: (state.paths || []).map(p => p.clone()),
            splines: (state.splines || [])
                .filter(s => keepLegs || s.type !== SketchSpline.TYPE_LEG)
                .map(s => s.clone()),
            closedShapes: (state.closedShapes || []).map(s => s.clone()),
            legShapes: keepLegs ? (state.legShapes || []).map(s => s.clone()) : []
        };
    }

    updateSketchRendering() {
        this.updateOffsetWidthWidget();
        this.updateCurveScrubWidget();
        if (!this.USE_UNIFIED_VIEW || !this.sketchRenderer3D) return;

        // LINKED MASTER STROKES: diff the live sketch into the stroke store
        // (write-back to masters / detach / add / delete), then materialize
        // fresh resolved states for EVERY layer. This replaces the old
        // "re-clone the live state onto every selected layer" stamping,
        // which clobbered diverged layers.
        this.syncStrokeStore();
    }

    /**
     * SYNC stage: diff the live sketch's committed strokes against the
     * stroke store, then resolve all per-layer states. Detaches are
     * surfaced with a toast. Safe to call repeatedly (idempotent when the
     * live sketch already matches the store).
     */
    syncStrokeStore() {
        const store = this.strokeStore;
        if (!store) return;
        store.ensureLayerCount(this.basePlanes.length);

        const selectedIndices = [...this.selectedBasePlaneIndices]
            .filter(i => i >= 0 && i < this.basePlanes.length)
            .sort((a, b) => a - b);

        let active = this.activeSketchLayerIndex;
        if (selectedIndices.length === 0 || !this.selectedBasePlaneIndices.has(active)) {
            // No trustworthy reference layer for diffing (e.g. mid selection
            // change): skip the mutation diff, just refresh resolved states
            this.resolveLayerStates();
            return;
        }

        // Committed live strokes only (in-progress drawings are excluded)
        const liveShapes = [...this.sketchPlane.splines, ...this.sketchPlane.paths];
        const detached = store.syncFromLive(liveShapes, selectedIndices, active);

        if (detached.length > 0) {
            console.log(`[LINKED STROKES] Stroke(s) detached from linked copies on layer ${active + 1}:`, detached);
            this.showToast('Stroke detached from linked copies — undo to re-link');
        }

        this.resolveLayerStates();
    }

    /**
     * RESOLUTION: materialize concrete per-layer sketch states from the
     * stroke store (masters + instances) into basePlaneSketchStates and
     * basePlanes[i].sketch, so every downstream stage (union, slices, leg
     * braces, exports, meshes, physics) consumes plain resolved shapes.
     */
    resolveLayerStates() {
        if (!this.strokeStore) return;
        this.strokeStore.ensureLayerCount(this.basePlanes.length);
        for (let i = 0; i < this.basePlanes.length; i++) {
            const state = this.strokeStore.resolveLayerState(i);
            this.basePlaneSketchStates[i] = state;
            this.basePlanes[i].sketch = state;
        }
    }

    /**
     * Load a layer's RESOLVED state into the live editable sketch and
     * remember it as the active reference layer for stroke diffing.
     * @param {number} index - Base plane index
     */
    loadLayerIntoSketch(index) {
        if (index < 0 || index >= this.basePlanes.length) return;
        this.activeSketchLayerIndex = index;
        this.sketchPlane.setSketchState(this.basePlaneSketchStates[index]);
    }

    /**
     * Commit a DIRECT store mutation (per-stroke modifier curve edit,
     * chair-curve slider, etc.): re-resolve every layer, reload the live
     * sketch from the active layer so the SYNC diff sees matching
     * geometry (a stale live sketch would read as a user edit and get
     * inverse-written into the master), re-select the previously selected
     * stroke by id, then run the canonical pipeline.
     * @returns {Promise} regenerateAll's pipeline promise
     */
    commitStoreMutation() {
        const sp = this.sketchPlane;
        const selectedId = (sp.selectedSpline || sp.selectedPath)?.strokeId || null;
        this.resolveLayerStates();
        this.loadLayerIntoSketch(this.activeSketchLayerIndex);
        if (selectedId) {
            const again = [...sp.splines, ...sp.paths].find(s => s.strokeId === selectedId);
            if (again) sp.selectShape(again);
        }
        return this.regenerateAll();
    }

    /**
     * Small transient toast notification (bottom-centre). Used to surface
     * stroke detach events. No-ops headless.
     * @param {string} message
     */
    showToast(message) {
        console.log('[TOAST]', message);
        if (typeof document === 'undefined') return;
        let el = document.getElementById('sketchchair-toast');
        if (!el) {
            el = document.createElement('div');
            el.id = 'sketchchair-toast';
            el.style.cssText = `
                position: fixed; left: 50%; bottom: 48px; transform: translateX(-50%);
                background: rgba(30, 30, 30, 0.92); color: #fff; border: 1px solid #00E5FF;
                border-radius: 8px; padding: 8px 14px; font-size: 13px; z-index: 200;
                pointer-events: none; opacity: 0; transition: opacity 0.25s ease;`;
            document.body.appendChild(el);
        }
        el.textContent = message;
        // Force a style flush so repeated toasts re-fade
        void el.offsetWidth;
        el.style.opacity = '1';
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { el.style.opacity = '0'; }, 3500);
    }

    /**
     * Render control points in 3D for selected planes when select tool is active (Phase 3.5+)
     */
    renderControlPoints() { return this.drawingPreview.renderControlPoints(); }

    /**
     * Create real-time drawing preview line (Phase 3.5)
     * @param {number} planeIndex - Index of the plane being drawn on
     */
    createDrawingPreview(planeIndex) { return this.drawingPreview.createDrawingPreview(planeIndex); }

    /**
     * Update real-time drawing preview line (Phase 3.5)
     * @param {number} planeIndex - Index of the plane being drawn on
     */
    updateDrawingPreview(planeIndex) { return this.drawingPreview.updateDrawingPreview(planeIndex); }

    /**
     * Build a preview group: red centerline + dark offset-outline loop
     * @param {Array} centerPoints - Centre path points (sketch pixels)
     * @param {Array|null} outlinePoints - Built offset outline points, if any
     * @param {number} zOffset - World Z to draw at
     * @returns {THREE.Group}
     */
    createPreviewGroup(centerPoints, outlinePoints, zOffset) { return this.drawingPreview.createPreviewGroup(centerPoints, outlinePoints, zOffset); }

    /**
     * Lightweight preview while dragging a control point with the select tool.
     * Shows the rebuilt outline live without regenerating meshes/slices
     * (full regeneration happens on pointer-up via onShapeChange).
     * @param {Object} spline - The spline being edited
     */
    updateSelectedSplinePreview(shape) { return this.drawingPreview.updateSelectedSplinePreview(shape); }

    /**
     * Sample a point list into a dense polyline, subdividing bezier
     * segments (cp2 of the segment start, cp1 of the segment end)
     * @param {Array} points - SketchPoints, possibly with control points
     * @returns {Array<{x,y}>}
     */
    sampleShapePoints(points) { return this.drawingPreview.sampleShapePoints(points); }

    /**
     * Remove real-time drawing preview (Phase 3.5)
     * @param {boolean} [clearPlane=true] - Also forget the preview plane index
     */
    removeDrawingPreview(clearPlane = true) { return this.drawingPreview.removeDrawingPreview(clearPlane); }

    /**
     * Create plane intersection demo
     * Shows two intersecting slice planes (forming an L-shape like a chair)
     */
    createPlaneIntersectionDemo() {
        // Clear physics cubes
        this.cubes.forEach(cube => {
            this.sceneManager.remove(cube);
            cube.geometry.dispose();
            cube.material.dispose();
        });
        this.cubes = [];

        // Create example chair with intersecting planes
        this.slicePlaneManager.createExampleChair();

        // Visualize the planes and their intersections
        this.visualizeSlicePlanes();

        console.log('Plane intersection demo created!');
        console.log('Stats:', this.slicePlaneManager.getStats());
    }

    /**
     * Initialize sketch canvas
     */
    initSketchCanvas() {
        // Size canvas to container
        const container = this.sketchCanvas.parentElement;
        this.sketchCanvas.width = container.clientWidth;
        this.sketchCanvas.height = container.clientHeight;

        // Initial render
        this.sketchPlane.render();

        // Setup resize handler
        window.addEventListener('resize', () => {
            this.sketchCanvas.width = container.clientWidth;
            this.sketchCanvas.height = container.clientHeight;
            this.sketchPlane.render();
        });

        // Setup callback for when shapes change (2D → 3D pipeline)
        this.sketchPlane.setOnShapeChange((closedShapes, splines, legShapes = []) => {
            // Update ALL selected planes with the current sketch
            const currentState = this.sketchPlane.getSketchState();
            for (const planeIndex of this.selectedBasePlaneIndices) {
                if (planeIndex >= 0 && planeIndex < this.basePlaneSketchStates.length) {
                    // Legs only land on outer layers (stripped for inner planes)
                    this.basePlaneSketchStates[planeIndex] = this.cloneSketchStateForPlane(currentState, planeIndex);
                }
            }

            // Regenerate 3D meshes for all selected planes
            this.updateMeshesFromAllSelectedPlanes();
        });

        console.log('Sketch canvas initialized:', this.sketchCanvas.width, 'x', this.sketchCanvas.height);
    }

    /**
     * Setup sketch callbacks for 3D mode (Phase 3.5)
     */
    setupSketchCallbacks() {
        // Clicking empty canvas with the select tool re-selects ALL layers,
        // restoring full opacity on every panel and rib
        this.sketchPlane.onEmptyCanvasClick = () => {
            const selectable = this.basePlanes.filter(p => !p.guide).length;
            if (this.selectedBasePlaneIndices.size < selectable) {
                this.selectAllPlanes();
            }
        };

        // Marquee (drag select) visual: dashed cyan rect in the overlay
        // frame at the front plane, updated live during the drag
        this.sketchPlane.onMarqueeUpdate = (m) => {
            if (this._marqueeRect) {
                this._marqueeRect.parent?.remove(this._marqueeRect);
                this._marqueeRect.geometry.dispose();
                this._marqueeRect.material.dispose();
                this._marqueeRect = null;
            }
            if (!m) return;
            const z = Math.max(...this.basePlanes.map(b => b.position.z)) + 0.04;
            const pts = [
                new THREE.Vector3(m.x0 * 0.01, m.y0 * 0.01, z),
                new THREE.Vector3(m.x1 * 0.01, m.y0 * 0.01, z),
                new THREE.Vector3(m.x1 * 0.01, m.y1 * 0.01, z),
                new THREE.Vector3(m.x0 * 0.01, m.y1 * 0.01, z),
                new THREE.Vector3(m.x0 * 0.01, m.y0 * 0.01, z)
            ];
            const geo = new THREE.BufferGeometry().setFromPoints(pts);
            const mat = new THREE.LineDashedMaterial({
                color: 0x00E5FF, dashSize: 0.06, gapSize: 0.04,
                depthTest: false, transparent: true, opacity: 0.9
            });
            const line = new THREE.Line(geo, mat);
            line.computeLineDistances();
            line.renderOrder = 1001;
            this._marqueeRect = line;
            this.addOverlay(line);
        };

        this.sketchPlane.setOnShapeChange(() => {
            // ALL mutations flow through the one canonical pipeline
            this.regenerateAll();
        });

        // Lightweight preview during select-tool drags - full regeneration on
        // every pointermove is far too slow (cross-slices + slots + meshes)
        this.sketchPlane.setOnDragPreview((spline) => {
            this.updateSelectedSplinePreview(spline);
        });

        // Undo snapshots: capture the committed state just before mutations
        this.sketchPlane.setOnBeforeCommit(() => this.pushUndoSnapshot());

        console.log('Sketch callbacks initialized for 3D mode');
    }

    /**
     * Scale a shape from canvas pixels to world units
     * Canvas pixels → World units: multiply by 0.01
     * @param {SketchShape} shape - Shape to scale
     * @returns {SketchShape} Scaled copy
     */
    scaleShapeToWorldUnits(shape) {
        const SCALE_FACTOR = 0.01; // 1 pixel = 0.01 units in 3D
        const scaledShape = shape.clone();

        // Scale all points
        scaledShape.points.forEach(point => {
            point.x *= SCALE_FACTOR;
            point.y *= SCALE_FACTOR;

            // Scale bezier control points if present
            if (point.controlPoint1) {
                point.controlPoint1.x *= SCALE_FACTOR;
                point.controlPoint1.y *= SCALE_FACTOR;
            }
            if (point.controlPoint2) {
                point.controlPoint2.x *= SCALE_FACTOR;
                point.controlPoint2.y *= SCALE_FACTOR;
            }
        });

        return scaledShape;
    }

    /**
     * Scale a spline from canvas pixels to world units
     * @param {SketchSpline} spline - Spline to scale
     * @returns {SketchSpline} Scaled copy
     */
    scaleSplineToWorldUnits(spline) {
        const SCALE_FACTOR = 0.01;
        const scaledSpline = spline.clone();

        // Scale center path points
        if (scaledSpline.centrePath && scaledSpline.centrePath.points) {
            scaledSpline.centrePath.points.forEach(point => {
                point.x *= SCALE_FACTOR;
                point.y *= SCALE_FACTOR;

                if (point.controlPoint1) {
                    point.controlPoint1.x *= SCALE_FACTOR;
                    point.controlPoint1.y *= SCALE_FACTOR;
                }
                if (point.controlPoint2) {
                    point.controlPoint2.x *= SCALE_FACTOR;
                    point.controlPoint2.y *= SCALE_FACTOR;
                }
            });
        }

        // Scale outline path points
        if (scaledSpline.path && scaledSpline.path.points) {
            scaledSpline.path.points.forEach(point => {
                point.x *= SCALE_FACTOR;
                point.y *= SCALE_FACTOR;

                if (point.controlPoint1) {
                    point.controlPoint1.x *= SCALE_FACTOR;
                    point.controlPoint1.y *= SCALE_FACTOR;
                }
                if (point.controlPoint2) {
                    point.controlPoint2.x *= SCALE_FACTOR;
                    point.controlPoint2.y *= SCALE_FACTOR;
                }
            });
        }

        // Scale brush width to world units
        if (scaledSpline.offsetSize) {
            scaledSpline.offsetSize *= SCALE_FACTOR;
        }

        return scaledSpline;
    }

    /**
     * Refresh every base plane's sketch for intersection detection.
     * LINKED MASTER STROKES: delegates to resolveLayerStates - each plane
     * gets its OWN resolved state (masters + instances, legs gated to
     * outer layers), never a stamped copy of the live sketch.
     * @param {Array} closedShapes - UNUSED (kept for call-site compat)
     * @param {Array} splines - UNUSED (kept for call-site compat)
     * @param {Array} legShapes - UNUSED (kept for call-site compat)
     */
    syncSketchToBasePlanes(closedShapes, splines, legShapes) {
        // LINKED MASTER STROKES: each plane's sketch is now its RESOLVED
        // state from the stroke store (masters + per-layer instances) - the
        // old "stamp the live closedShapes to every plane" copy clobbered
        // layers that had deliberately diverged (detached strokes). The
        // arguments are kept for call-site compatibility but unused.
        this.resolveLayerStates();
    }

    /**
     * Automatically create primary slice plane from completed brush stroke
     * This creates the base plane that cross-slices will intersect with
     * AND automatically generates cross-slice groups
     */
    autoCreatePrimarySlicePlane() {
        const closedShapes = this.sketchPlane.closedShapes;
        const splines = this.sketchPlane.splines;
        const legShapes = this.sketchPlane.legShapes || [];

        // Only create if we have closed shapes
        if (closedShapes.length === 0 && legShapes.length === 0) {
            // Sketch is empty (e.g. last shape deleted): clear stale
            // cross-slices and their meshes instead of leaving orphan ribs.
            // Bump the generation so any in-flight async regeneration from a
            // previous sketch change aborts instead of re-adding planes.
            this._crossSliceGeneration = (this._crossSliceGeneration || 0) + 1;
            if (this.slicePlaneManager.planes.length > 0) {
                this.slicePlaneManager.clear();
                this.slicePlaneManager.slots = [];
                this.crossSliceSelections = [];
                this.regenerateMeshesFromCurrentSketch();
            }
            return;
        }

        // Check if we already have a primary plane
        if (this.slicePlaneManager.planes.length > 0) {
            // Update existing primary plane with current sketch data
            const primaryPlane = this.slicePlaneManager.planes[0];

            // Create a snapshot of the current sketch state
            if (!primaryPlane.sketch) {
                primaryPlane.sketch = {};
            }

            // Clone the shapes (keep in canvas pixels - will be scaled during mesh generation)
            primaryPlane.sketch.closedShapes = closedShapes.map(shape => shape.clone());
            primaryPlane.sketch.splines = splines.map(spline => spline.clone());
            primaryPlane.sketch.legShapes = legShapes.map(shape => shape.clone()); // Store leg shapes

            // IMPORTANT: Also update ALL base planes with the new sketch data
            console.log('🔍 Current selectedBasePlaneIndices:', Array.from(this.selectedBasePlaneIndices));

            // Legs go only to selected OUTER layers; unselected planes keep
            // the legs they already own
            this.syncSketchToBasePlanes(closedShapes, splines, legShapes);

            console.log('Updated existing primary plane with new sketch data');
            console.log('   - Closed shapes:', closedShapes.length);
            console.log('   - Leg shapes:', legShapes.length);
            console.log(`   - Updated ${this.basePlanes.length} base planes`);

            // Debug: Show which planes got legs
            for (let i = 0; i < this.basePlanes.length; i++) {
                const legCount = (this.basePlanes[i].sketch.legShapes || []).length;
                if (legCount > 0) {
                    console.log(`   - basePlane[${i}]: ${legCount} legs (selected: ${this.selectedBasePlaneIndices.has(i)})`);
                }
            }

            // Regenerate cross-slices for updated shape (returns the async
            // regeneration promise so callers CAN await the settled state;
            // fire-and-forget callers are unaffected)
            return this.autoGenerateCrossSlices(primaryPlane);
        }

        // Create a primary SlicePlane from the sketch
        // Position it at Z=0 (XY plane) with normal pointing up
        const primaryPlane = new SlicePlane({
            position: new THREE.Vector3(0, 0, 0),
            normal: new THREE.Vector3(0, 0, 1), // Normal pointing up (Z-axis)
            thickness: this.getEffectiveThickness?.() ?? this.materialThickness ?? 5.0, // Material thickness in mm
            guide: true, // This is a guide plane (base layer)
            selected: false
        });

        // Store sketch data directly on the plane (not just a reference)
        // This must be a plain object with closedShapes for intersection detection
        // Keep in canvas pixels - will be scaled during mesh generation AND intersection detection
        primaryPlane.sketch = {
            closedShapes: closedShapes.map(shape => shape.clone()),
            splines: splines.map(spline => spline.clone()),
            legShapes: legShapes.map(shape => shape.clone())
        };

        // IMPORTANT: Copy sketch data to ALL base planes for intersection detection
        // Each base plane at different Z levels needs the same 2D sketch geometry.
        // Legs go only to selected OUTER layers; unselected planes keep theirs.
        this.syncSketchToBasePlanes(closedShapes, splines, legShapes);
        console.log(`✅ Copied sketch data to ${this.basePlanes.length} base planes for intersection detection`);

        // Debug: Show which planes got legs
        for (let i = 0; i < this.basePlanes.length; i++) {
            const legCount = (this.basePlanes[i].sketch.legShapes || []).length;
            if (legCount > 0) {
                console.log(`   - basePlane[${i}]: ${legCount} legs (selected: ${this.selectedBasePlaneIndices.has(i)})`);
            }
        }

        // Add to slice plane manager
        this.slicePlaneManager.addPlane(primaryPlane);

        console.log('✅ Auto-created primary slice plane from brush stroke');
        console.log('   - Plane position: (0, 0, 0)');
        console.log('   - Plane normal: (0, 0, 1)');
        console.log('   - Closed shapes:', closedShapes.length);
        console.log('   - Splines:', splines.length);

        // AUTO-GENERATE CROSS-SLICES (promise returned so callers can await)
        return this.autoGenerateCrossSlices(primaryPlane);
    }

    /**
     * Automatically generate cross-slice groups from primary plane
     * Regenerates ALL CrossSliceSelections (ribs, single slices, slats),
     * each along its own guide path. If no slice group exists yet, a
     * default auto rib group is created (original behavior).
     * @param {SlicePlane} primaryPlane - The primary plane with sketch data
     */
    async autoGenerateCrossSlices(primaryPlane) {
        if (!primaryPlane.sketch || !primaryPlane.sketch.closedShapes || primaryPlane.sketch.closedShapes.length === 0) {
            console.warn('No closed shapes for cross-slice generation');
            return;
        }

        // This method is async and can be superseded by a newer sketch
        // change (another stroke, a deletion) while awaiting. Track a
        // generation number; stale runs abort instead of re-adding planes.
        const generation = this._crossSliceGeneration = (this._crossSliceGeneration || 0) + 1;
        const isStale = () => generation !== this._crossSliceGeneration;

        // Clear existing cross-slice PLANES (but keep CrossSliceSelections)
        console.log('🔄 Clearing existing cross-slice planes before regeneration...');
        this.slicePlaneManager.clear();

        // Import SketchSpline to check for leg type
        const { SketchSpline } = await import('../sketch/SketchSpline.js');
        const { CrossSliceSelection } = await import('../geometry/CrossSliceSelection.js');
        const { SliceType } = await import('../geometry/SliceType.js');

        // Types of selections regenerated by this pipeline (legs are handled
        // separately by generateLegCrossSlices)
        const sliceGroupTypes = [SliceType.SLICES, SliceType.SINGLE_SLICE, SliceType.SLATSLICES];

        // If there are no slice groups yet, create the default auto rib group
        const hasSliceGroups = this.crossSliceSelections.some(sel => sliceGroupTypes.includes(sel.type));
        if (!hasSliceGroups) {
            console.log('   - Creating default CrossSliceSelection for ribs');

            // Find the first NON-LEG spline to use as the generating shape.
            // Legs should not be used as guide paths for regular cross-slices.
            let shapeIndex = -1;
            if (primaryPlane.sketch.splines) {
                for (let i = 0; i < primaryPlane.sketch.splines.length; i++) {
                    if (primaryPlane.sketch.splines[i].type !== SketchSpline.TYPE_LEG) {
                        shapeIndex = i;
                        break;
                    }
                }
            }

            // Create a cross-slice selection with default settings.
            // The guide path is resolved fresh per regeneration below.
            const selection = new CrossSliceSelection({
                path: null,
                plane: primaryPlane,
                start: 0,          // Start at the beginning of the path
                end: 1,            // End at the end of the path
                spacing: 50,       // 50mm spacing between ribs
                type: SliceType.SLICES
            });

            // Set default options
            selection.smooth = true;
            selection.generateFlushTops = false;
            selection.flipSide = false;
            // Clip to the shape that generated the slices ("through all" off)
            selection.cropToCurrentShape = true;
            selection.shapeIndex = Math.max(0, shapeIndex);

            // Store the CrossSliceSelection for future regenerations
            this.crossSliceSelections.push(selection);
        }

        // Generate the LEG brace slices FIRST (one rib per drawn leg,
        // running along the leg and connecting the mirrored pair - Java
        // parity). Order matters: leg slices have CULLING PRIORITY over
        // regular ribs (Java tiedToLeg semantics) - because the leg planes
        // are already in the manager when the regular groups run,
        // removeCollidingRibs seeds from them and drops any regular rib
        // whose chord hits a leg brace's footprint. Leg planes themselves
        // never pass through that culling, so they can never lose.
        const allGeneratedPlanes = [];
        const legPlanes = await this.generateLegCrossSlices(primaryPlane, isStale);
        if (isStale()) {
            console.log('Cross-slice generation superseded during leg slice build, aborting');
            return;
        }
        allGeneratedPlanes.push(...legPlanes);

        // Regenerate EVERY slice group. Sketch shapes are cloned on every
        // edit, so each selection's guide path must be re-resolved against
        // the CURRENT sketch objects (stale path references are the norm).
        for (const selection of this.crossSliceSelections) {
            if (!sliceGroupTypes.includes(selection.type)) continue;

            const guide = this.resolveCrossSliceGuide(selection.shapeIndex ?? 0, primaryPlane, SketchSpline);
            if (!guide) {
                console.warn(`❌ No suitable guide path for slice group (shapeIndex ${selection.shapeIndex}) - skipping`);
                selection.planes = [];
                continue;
            }

            console.log(`🔄 Regenerating slice group type ${selection.type} (shape ${selection.shapeIndex ?? 0}, ${guide.guideSpline ? 'spline centerline' : 'closed shape outline'})...`);

            // SketchPath/SketchShape objects don't have a .path property -
            // they ARE the path. Wrap so GeometryOperations can access it.
            selection.path = { path: guide.guidePath };
            selection.plane = primaryPlane;
            selection.strokeWidth = guide.guideSpline ? guide.guideSpline.offsetSize * 2 : null;
            // Per-point widths (spline.centreOffset) make strokeWidth vary
            // along the path - keep the spline so rib culling can use the
            // LOCAL width at each rib's position
            selection.guideSpline = guide.guideSpline || null;

            let generatedPlanes;
            if (selection.type === SliceType.SLATSLICES) {
                generatedPlanes = this.slicePlaneManager.generateSlatSlices(selection);
            } else {
                // Generate the cross-slices using the selection (aborts
                // mid-loop if a newer sketch change supersedes this run)
                generatedPlanes = await this.slicePlaneManager.generateSlicesAlongPath(selection, isStale);
            }

            if (isStale()) {
                console.log('Cross-slice generation superseded by a newer sketch change, aborting');
                return;
            }

            allGeneratedPlanes.push(...generatedPlanes);
            console.log(`✅ Generated ${generatedPlanes.length} slices for group`);
        }

        const generatedPlanes = allGeneratedPlanes;
        console.log(`✅ Auto-generated ${generatedPlanes.length} cross-slices across ${this.crossSliceSelections.length} selection(s) (${legPlanes.length} leg brace(s), generated first for culling priority)`);

        // Refresh the slice list. NOTE: the panel lives at
        // uiManager.panels.slices - there is no uiManager.slicesPanel, so the
        // old guard was always false and this block never ran. The slice list
        // then only refreshed when something else called update() (a panel
        // interaction, or opening the section via toggleSection), which is why
        // the list "sometimes" needed a collapse/expand to appear.
        //
        // Deliberately NOT calling visualizePlanes() here: it drew placeholder
        // translucent-green debug quads at each plane, obsolete now that the
        // real rib meshes are generated (generateMeshesForAllSlicePlanes). It
        // had been dead alongside the broken guard; reviving it flooded the
        // scene green. Clear any quads a stale build may have left behind.
        this.uiManager?.panels?.slices?.update();
        if (this.planeVisualizations?.length) {
            for (const mesh of this.planeVisualizations) {
                this.sceneManager?.remove(mesh);
                mesh.geometry?.dispose();
                mesh.material?.dispose();
            }
            this.planeVisualizations = [];
        }

        // Re-render the base-plane panels FIRST so they pick up the freshly
        // generated waffle slots (this clears generatedMeshes), then append
        // the cross-slice meshes.
        this.regenerateMeshesFromCurrentSketch();

        // Generate 3D meshes for all planes
        this.generateMeshesForAllSlicePlanes();
    }

    /**
     * Resolve the guide path for a slice group against the CURRENT sketch.
     * Shapes are cloned on every edit, so stored path references go stale;
     * this re-resolves by shapeIndex each regeneration.
     * Prefers the live sketch plane, falling back to the primary plane's
     * sketch snapshot (used e.g. when regenerating from base planes).
     * @param {number} shapeIndex - Index of the generating shape
     * @param {SlicePlane} primaryPlane - Plane with a sketch snapshot fallback
     * @param {Class} SketchSpline - SketchSpline class (for TYPE_LEG check)
     * @returns {{guidePath: Object, guideSpline: Object|null}|null}
     */
    resolveCrossSliceGuide(shapeIndex, primaryPlane, SketchSpline) {
        const liveSketch = this.sketchPlane;
        const sketch = (liveSketch && ((liveSketch.closedShapes && liveSketch.closedShapes.length > 0) ||
                                       (liveSketch.splines && liveSketch.splines.length > 0)))
            ? liveSketch
            : primaryPlane.sketch;
        if (!sketch) return null;

        const splines = sketch.splines || [];
        const closedShapes = sketch.closedShapes || [];

        // Prefer the spline centerline at this index (unless it's a leg)
        const spline = splines[shapeIndex];
        if (spline && spline.type !== SketchSpline.TYPE_LEG &&
            spline.centrePath && spline.centrePath.points && spline.centrePath.points.length > 0) {
            return { guidePath: spline.centrePath, guideSpline: spline };
        }

        // Fall back to the closed shape outline at this index
        if (closedShapes[shapeIndex]) {
            return { guidePath: closedShapes[shapeIndex], guideSpline: null };
        }

        return null;
    }

    /**
     * Generate cross-slices for leg reinforcement
     * Creates perpendicular ribs along each leg's center path
     * @param {SlicePlane} primaryPlane - The primary plane with leg shapes
     */
    async generateLegCrossSlices(primaryPlane, isStale = () => false) {
        const { SketchSpline } = await import('../sketch/SketchSpline.js');
        const { CrossSliceSelection } = await import('../geometry/CrossSliceSelection.js');
        const { SliceType } = await import('../geometry/SliceType.js');

        // Legs are sourced from the OUTER plane's stored sketch state - the
        // layer that OWNS legs - falling back to the primary plane's sketch.
        // Sourcing from the live/primary sketch alone made braces vanish
        // whenever an INNER (legless) layer was the active selection during
        // a pipeline run. (Splines are cloned on every edit, so leg
        // selections are keyed by leg INDEX, not object identity - same
        // convention as slice groups' shapeIndex.)
        const outerSplines = this.basePlaneSketchStates?.[0]?.splines || [];
        const sourceSplines = outerSplines.some(s => s.type === SketchSpline.TYPE_LEG)
            ? outerSplines
            : (primaryPlane.sketch?.splines || []);
        const legSplines = sourceSplines.filter(
            spline => spline.type === SketchSpline.TYPE_LEG
        );

        // Fresh debug records for the leg-brace overlay ('j' key)
        this.slicePlaneManager.legBraceDebug = [];

        // Drop selections whose leg no longer exists (leg deleted / undo).
        // A shrinking leg count also invalidates the "user deleted this
        // brace" suppression indexes - clear them rather than suppressing
        // the wrong leg.
        if (this._lastLegCount !== undefined && legSplines.length < this._lastLegCount) {
            this.disabledLegSlices?.clear();
        }
        this._lastLegCount = legSplines.length;
        this.crossSliceSelections = this.crossSliceSelections.filter(sel =>
            sel.type !== SliceType.LEG || (sel.legIndex ?? 0) < legSplines.length);

        if (legSplines.length === 0) {
            this.renderLegBraceDebug(); // clear a stale overlay
            return [];
        }

        const generated = [];
        for (let legIndex = 0; legIndex < legSplines.length; legIndex++) {
            const legSpline = legSplines[legIndex];
            // User explicitly deleted this leg's brace group: stay deleted
            // until the legs change (session-scoped, not persisted)
            if (this.disabledLegSlices?.has(legIndex)) continue;

            let selection = this.crossSliceSelections.find(sel =>
                sel.type === SliceType.LEG && (sel.legIndex ?? 0) === legIndex);

            if (!selection) {
                selection = new CrossSliceSelection({
                    path: null,
                    plane: primaryPlane,
                    start: 0,
                    end: 1,
                    spacing: 0,
                    type: SliceType.LEG
                });
                selection.tieToLeg = true;
                selection.cropToCurrentShape = false; // Java: leg slices span all shapes
                selection.legIndex = legIndex;
                // Crossbrace between the feet (off by default; mm inputs)
                selection.crossbrace = false;
                selection.crossbraceWidth = 40;
                selection.crossbraceBottomOffset = 20;
                this.crossSliceSelections.push(selection);
                console.log(`   - Created LEG brace selection for leg ${legIndex}`);
            }

            selection.legSpline = legSpline;
            selection.plane = primaryPlane;
            selection.planes = [];

            // STALE GUARD: a superseding edit may have started a newer
            // pipeline run while we were awaiting - adding this (now stale)
            // brace after the newer run's clear() would DUPLICATE leg
            // planes. Bail before every build AND before every addPlane.
            if (isStale()) {
                console.log('generateLegCrossSlices superseded, aborting');
                return generated;
            }

            // A failure building ONE brace must never abort the whole
            // regeneration pipeline (the slice planes were already cleared -
            // an escaping exception here would leave the chair half-built)
            try {
                const plane = await this.slicePlaneManager.buildLegSlicePlane(
                    selection,
                    legSpline,
                    this.getEffectiveThickness?.() ?? 5.0
                );
                if (isStale()) {
                    console.log('generateLegCrossSlices superseded after build, aborting');
                    return generated;
                }
                if (plane) {
                    this.slicePlaneManager.addPlane(plane);
                    selection.planes.push(plane);
                    generated.push(plane);
                }
            } catch (error) {
                console.error(`Leg brace generation failed for leg ${legIndex} (continuing):`, error);
                // Surface the exception in the debug overlay/logging too
                const rec = (this.slicePlaneManager.legBraceDebug || [])
                    .find(d => d.legIndex === legIndex);
                if (rec && !rec.bail) rec.bail = `exception: ${error?.message || error}`;
            }
        }

        // Refresh the leg-brace debug overlay + structured log if enabled
        this.renderLegBraceDebug();

        console.log(`✅ Generated ${generated.length} leg brace slice(s) for ${legSplines.length} leg(s)`);
        return generated;
    }

    /**
     * Regenerate 3D meshes from current sketch on all selected planes
     */
    regenerateMeshesFromCurrentSketch() {
        const currentState = this.sketchPlane.getSketchState();

        // Clear old physics bodies before regenerating
        this.clearChairPhysicsBodies();

        // Remove old generated meshes from scene
        this.generatedMeshes.forEach(mesh => {
            this.sceneManager.remove(mesh);
            mesh.geometry?.dispose();
            mesh.material?.dispose();
        });
        this.generatedMeshes = [];

        // Regenerate meshes for ALL base planes - each plane renders its own
        // stored sketch state. Iterating only the SELECTED planes here made
        // the unselected panels vanish whenever the selection changed (e.g.
        // the leg tool selecting outer layers), because all meshes were
        // cleared above but only selected planes were rebuilt.
        for (let planeIndex = 0; planeIndex < this.basePlanes.length; planeIndex++) {
            const basePlane = this.basePlanes[planeIndex];
            const sketchState = this.basePlaneSketchStates[planeIndex];

            console.log(`🔍 Processing plane ${planeIndex}:`, {
                hasSketchState: !!sketchState,
                closedShapes: sketchState?.closedShapes?.length || 0,
                legShapes: sketchState?.legShapes?.length || 0,
                zPosition: basePlane.position.z
            });

            // Skip guide layers
            if (basePlane.guide) {
                console.log(`Skipping guide layer ${planeIndex + 1} for mesh generation`);
                continue;
            }

            // A plane has fabricable content when it has strokes OR legs -
            // requiring closedShapes made legs-only planes (e.g. legs drawn
            // before/without a body stroke) silently produce no geometry
            const stateShapes = sketchState?.closedShapes || [];
            const stateLegs = sketchState?.legShapes || [];
            if (stateShapes.length > 0 || stateLegs.length > 0) {
                const allShapes = [...stateShapes, ...stateLegs];

                this.generateMeshesForPlane(basePlane, allShapes, planeIndex);
                console.log(`✅ Regenerated ${allShapes.length} meshes for plane ${planeIndex} at Z=${basePlane.position.z} (${stateLegs.length} legs)`);
            }
        }

        // These meshes are built in the generated frame, and the compound body
        // that would carry them to the parked pose is only rebuilt later (async
        // slice tail) - hold them at the parked pose meanwhile so a hand-moved
        // chair does not flash back to its pre-drag spot on every edit
        this.parkGeneratedMeshes();

        // Keep the rotation pivot on the chair centre
        this.updateRotationPivot();
    }

    /**
     * Copy current sketch to clipboard
     */
    copySketch() {
        const state = this.sketchPlane.getSketchState();

        // Deep clone the sketch state
        this.clipboard = {
            paths: state.paths.map(p => p.clone()),
            splines: state.splines.map(s => s.clone()),
            closedShapes: state.closedShapes.map(s => s.clone()),
            legShapes: state.legShapes ? state.legShapes.map(s => s.clone()) : []
        };

        console.log(`Copied sketch: ${state.paths.length} paths, ${state.splines.length} splines, ${state.legShapes?.length || 0} legs`);
    }

    /**
     * Paste clipboard sketch to current plane(s)
     */
    pasteSketch() {
        if (!this.clipboard) {
            console.log('Clipboard is empty');
            return;
        }

        this.pushUndoSnapshot();

        // Pasted strokes are NEW strokes: fresh masters (fresh ids, so they
        // never alias whatever was copied), linked on all selected layers.
        // replaceLayerStrokes swaps the selected layers' instance tables
        // wholesale (legs land only on outer layers).
        const shapes = [
            ...(this.clipboard.splines || []).map(s => s.clone()),
            ...(this.clipboard.paths || []).map(p => p.clone())
        ];
        shapes.forEach(s => { s.strokeId = generateStrokeId(); });

        const selectedIndices = [...this.selectedBasePlaneIndices].sort((a, b) => a - b);
        this.strokeStore.ensureLayerCount(this.basePlanes.length);
        this.strokeStore.replaceLayerStrokes(selectedIndices, shapes);
        this.resolveLayerStates();

        // Show the active layer's resolved paste result in the live sketch
        const active = this.selectedBasePlaneIndices.has(this.activeSketchLayerIndex)
            ? this.activeSketchLayerIndex
            : (selectedIndices[0] ?? 0);
        this.loadLayerIntoSketch(active);

        // Canonical pipeline: paste is a mutation like any other (the SYNC
        // stage is a no-op here because live already matches the store)
        this.regenerateAll();

        const planeCount = this.selectedBasePlaneIndices.size;
        console.log(`Pasted sketch to ${planeCount} plane${planeCount > 1 ? 's' : ''}`);
    }

    /**
     * Update 3D meshes for all selected base planes
     */
    updateMeshesFromAllSelectedPlanes() {
        // Clear old chair physics bodies
        this.clearChairPhysicsBodies();

        // Remove old generated meshes from scene
        this.generatedMeshes.forEach(mesh => {
            this.sceneManager.remove(mesh);
            mesh.geometry.dispose();
            mesh.material.dispose();
        });
        this.generatedMeshes = [];

        // Generate meshes for ALL planes with content - unselected layers
        // still render, dimmed by updateLayerMeshOpacity, so the whole
        // design stays visible while editing a subset of layers
        for (let planeIndex = 0; planeIndex < this.basePlanes.length; planeIndex++) {

            const basePlane = this.basePlanes[planeIndex];
            const sketchState = this.basePlaneSketchStates[planeIndex];

            // Skip guide layers (they affect cross-slices but don't generate visible meshes)
            if (basePlane.guide) {
                console.log(`Skipping guide layer ${planeIndex + 1} for mesh generation`);
                continue;
            }

            // Strokes OR legs both count as content (legs-only planes must
            // still fabricate - see regenerateMeshesFromCurrentSketch)
            const stateShapes = sketchState?.closedShapes || [];
            const stateLegs = sketchState?.legShapes || [];
            if (stateShapes.length === 0 && stateLegs.length === 0) {
                continue; // Skip empty planes
            }

            const allShapes = [...stateShapes, ...stateLegs];

            this.generateMeshesForPlane(basePlane, allShapes, planeIndex);
        }

        console.log(`Generated ${this.generatedMeshes.length} total meshes for ${this.selectedBasePlaneIndices.size} selected planes`);

        // Rebuild rib meshes too - the clear above removed them, and layer
        // selection changes previously left the chair rib-less until the
        // next shape change
        if (this.slicePlaneManager?.planes?.length) {
            this.generateMeshesForAllSlicePlanes();
        }
    }

    /**
     * Generate 3D meshes for a specific plane
     * @param {SlicePlane} basePlane - The base plane
     * @param {Array} closedShapes - Array of closed shapes
     * @param {number} planeIndex - Index of the plane
     */
    generateMeshesForPlane(basePlane, closedShapes, planeIndex) {
        const materialThickness = basePlane.thickness * 0.01; // Convert mm to world units
        const scaleFactor = 0.01; // 1 pixel = 0.01 units in 3D

        // Collect waffle-joint slot cutouts for this plane (profile-frame
        // pixels, scaled to mesh units). These get boolean-subtracted from
        // the part outline so the fabricated pieces actually interlock.
        // exportWithoutSlots (set temporarily by the STL "Assembled, no
        // slots" export mode) skips the cutouts entirely.
        const slotCutouts = [];
        if (!this.exportWithoutSlots && basePlane.slots && basePlane.slots.getAll) {
            for (const slot of basePlane.slots.getAll()) {
                for (const ring of slot.getAllOutlines()) {
                    slotCutouts.push(ring.map(p => ({
                        x: p.x * scaleFactor,
                        y: p.y * scaleFactor
                    })));
                }
            }
        }
        if (slotCutouts.length > 0) {
            console.log(`   - Cutting ${slotCutouts.length} slot(s) into plane ${planeIndex + 1}`);
        }

        // Sample every shape on this plane (outer ring + hole rings, beziers
        // subdivided with the same sampler the exporters use) and UNION them:
        // overlapping strokes and legs must fabricate as ONE piece with a
        // single cutting outline. Disjoint shapes still come back as
        // separate polygons (separate meshes/pieces).
        const shapePolygons = [];
        for (const shape of closedShapes) {
            if (!shape || !shape.points || shape.points.length < 3) continue;
            const outer = sampleRing(shape.points).map(p => ({
                x: p.x * scaleFactor,
                y: p.y * scaleFactor
            }));
            if (outer.length < 3) continue;
            // Closed strokes carry their inner outline as a hole
            const holes = (shape.holes || [])
                .map(hole => sampleRing(hole).map(p => ({
                    x: p.x * scaleFactor,
                    y: p.y * scaleFactor
                })))
                .filter(hole => hole.length >= 3);
            shapePolygons.push({ outer, holes });
        }

        const unionedPolygons = unionShapes(shapePolygons);
        if (shapePolygons.length > unionedPolygons.length) {
            console.log(`   - Unioned ${shapePolygons.length} shapes into ${unionedPolygons.length} piece(s) on plane ${planeIndex + 1}`);
        }

        unionedPolygons.forEach((polygon, shapeIndex) => {
            try {
                // Subtract slot cutouts (waffle joints) from the unioned outline
                const polygons = slotCutouts.length > 0
                    ? subtractCutouts(polygon.outer, slotCutouts, polygon.holes)
                    : [polygon];

                // Generate 3D mesh with extrusion
                const mesh = this.meshBuilder.createMeshFromPolygons(polygons, {
                    depth: materialThickness
                });

                if (mesh) {
                    // Log geometry bounds BEFORE any transformations
                    mesh.geometry.computeBoundingBox();
                    const bbox = mesh.geometry.boundingBox;
                    console.log(`Mesh ${planeIndex + 1} geometry bounds (BEFORE transforms):`, {
                        x: `[${bbox.min.x.toFixed(1)}, ${bbox.max.x.toFixed(1)}]`,
                        y: `[${bbox.min.y.toFixed(1)}, ${bbox.max.y.toFixed(1)}]`,
                        z: `[${bbox.min.z.toFixed(1)}, ${bbox.max.z.toFixed(1)}]`
                    });

                    // Position mesh on the base plane, CENTERED on the plane
                    // position. The extrusion is z-centred (+-t/2, see
                    // buildExtrudedGeometry), and rib slots are cut centred
                    // on the base plane's z (addWaffleSlot) - shifting the
                    // panel by -t/2 here left every panel half a material
                    // thickness off-centre in its slots.
                    // For cross-slices (vertical planes), center them vertically
                    const isVerticalPlane = basePlane.normal && Math.abs(basePlane.normal.z) < 0.1;
                    const zPosition = isVerticalPlane ? 0 : basePlane.position.z;

                    mesh.position.set(
                        basePlane.position.x,
                        basePlane.position.y,
                        zPosition
                    );

                    console.log(`Mesh ${planeIndex + 1} position:`, {
                        x: mesh.position.x.toFixed(3),
                        y: mesh.position.y.toFixed(3),
                        z: mesh.position.z.toFixed(3),
                        isVertical: isVerticalPlane,
                        color: isVerticalPlane ? 'ORANGE (cross-slice)' : 'GREEN/BLUE (primary)'
                    });

                    // Rotate mesh to match plane's normal orientation
                    // Default mesh: XY plane with normal pointing up (0,0,1)
                    // Target: Plane with given normal
                    if (basePlane.normal) {
                        const targetNormal = basePlane.normal.clone().normalize();

                        console.log(`Plane ${planeIndex + 1} normal:`, {
                            x: targetNormal.x.toFixed(3),
                            y: targetNormal.y.toFixed(3),
                            z: targetNormal.z.toFixed(3)
                        });

                        // Check if this is a cross-slice (vertical plane with horizontal normal)
                        const isVerticalPlane = Math.abs(targetNormal.z) < 0.1;

                        if (isVerticalPlane) {
                            // For vertical cross-slices:
                            // The normal points horizontally (perpendicular to the vertical plane)
                            // We want the plane to face perpendicular to this normal

                            const normalAngle = Math.atan2(targetNormal.y, targetNormal.x);

                            // Rotate around Z first to orient in XY plane, then around X to stand upright
                            // Use ZXY order so Z rotation happens first, then X
                            // Add 90° correction because plane normal is set to tangent (not perpendicular)
                            mesh.rotation.order = 'ZXY';
                            mesh.rotation.set(
                                Math.PI / 2,                // 90° around X (stand upright) - applied second
                                0,                          // No Y rotation
                                normalAngle - Math.PI / 2   // Rotate around Z + 90° clockwise correction - applied first
                            );

                            console.log(`Plane ${planeIndex + 1} rotation (vertical):`, {
                                x: '90.0°',
                                y: '0.0°',
                                z: THREE.MathUtils.radToDeg(normalAngle).toFixed(1) + '°',
                                normalAngle: THREE.MathUtils.radToDeg(normalAngle).toFixed(1) + '°',
                                normal: `(${targetNormal.x.toFixed(3)}, ${targetNormal.y.toFixed(3)}, ${targetNormal.z.toFixed(3)})`
                            });

                            // VERIFY rotation was applied
                            console.log(`Plane ${planeIndex + 1} ACTUAL mesh rotation AFTER setting:`, {
                                x: THREE.MathUtils.radToDeg(mesh.rotation.x).toFixed(1) + '°',
                                y: THREE.MathUtils.radToDeg(mesh.rotation.y).toFixed(1) + '°',
                                z: THREE.MathUtils.radToDeg(mesh.rotation.z).toFixed(1) + '°'
                            });
                        } else {
                            // For horizontal planes: no rotation needed (they're already in XY plane)
                            console.log(`Plane ${planeIndex + 1} rotation (horizontal):`, {
                                x: '0.0°',
                                y: '0.0°',
                                z: '0.0°',
                                normal: `(${targetNormal.x.toFixed(3)}, ${targetNormal.y.toFixed(3)}, ${targetNormal.z.toFixed(3)})`
                            });
                        }
                    }

                    // Tag panels with their layer; rib planes (profile
                    // frame) have no layer - they dim only when a layer
                    // subset is focused
                    if (!basePlane.profileXAxis) {
                        mesh.userData.basePlaneIndex = planeIndex;
                    }
                    this.sceneManager.add(mesh);
                    this.generatedMeshes.push(mesh);

                    // Update mesh matrices to ensure quaternion is synced from euler angles
                    mesh.updateMatrix();
                    mesh.updateMatrixWorld(true);

                    // NOTE: Physics body will be created as compound body after all meshes are generated

                    // VERIFY rotation AFTER adding to scene
                    console.log(`Plane ${planeIndex + 1} rotation AFTER scene add:`, {
                        x: THREE.MathUtils.radToDeg(mesh.rotation.x).toFixed(1) + '°',
                        y: THREE.MathUtils.radToDeg(mesh.rotation.y).toFixed(1) + '°',
                        z: THREE.MathUtils.radToDeg(mesh.rotation.z).toFixed(1) + '°'
                    });

                    console.log(`Generated mesh for plane ${planeIndex + 1}, shape ${shapeIndex}: Z=${basePlane.position.z.toFixed(3)}`);
                }
            } catch (error) {
                console.error(`Failed to generate mesh for plane ${planeIndex}, shape ${shapeIndex}:`, error);
            }
        });

        // Don't auto-position ragdoll - only position during stability testing
        // (positionOnChair is called in testChairStability instead)
    }

    /**
     * Generate 3D meshes for ALL slice planes in the SlicePlaneManager
     * This creates the actual 3D geometry for all slices (primary + cross-slices)
     *
     * NOTE: Currently only generates meshes for primary plane with sketchData.
     * Cross-slice meshes require building slice paths via plane intersections (Phase 4).
     */
    generateMeshesForAllSlicePlanes() {
        if (!this.slicePlaneManager || this.slicePlaneManager.planes.length === 0) {
            console.log('No slice planes to generate meshes for');
            return;
        }

        // Pause physics if running to prevent aliasing errors
        // (can't modify bodies while world is being stepped)
        const wasPhysicsPlaying = this.isPhysicsPlaying;
        if (wasPhysicsPlaying) {
            this.isPhysicsPlaying = false;
            console.log('Paused physics for mesh regeneration');
        }

        // Clear old physics bodies before regenerating meshes
        // This prevents Rapier aliasing errors when creating new compound bodies
        this.clearChairPhysicsBodies();

        console.log(`🔨 Checking ${this.slicePlaneManager.planes.length} slice planes for mesh generation...`);

        let meshCount = 0;
        let crossSliceCount = 0;
        this.slicePlaneManager.planes.forEach((plane, index) => {
            // Generate meshes for ALL planes with sketch data (primary AND cross-slices)
            if (plane.sketch && plane.sketch.closedShapes && plane.sketch.closedShapes.length > 0) {
                // For PRIMARY plane (index 0): SKIP leg shapes - they belong to basePlanes, not the primary plane
                // For CROSS-SLICES: include all shapes
                const allShapes = [...plane.sketch.closedShapes];

                if (index !== 0) {
                    // Cross-slices can have leg shapes (if they intersect legs)
                    const legShapes = plane.sketch.legShapes || [];
                    if (legShapes.length > 0) {
                        allShapes.push(...legShapes);
                    }
                }

                // Generate meshes for this plane
                this.generateMeshesForPlane(plane, allShapes, index);
                meshCount += allShapes.length;

                if (index === 0) {
                    console.log(`   - Generated meshes for PRIMARY plane: ${allShapes.length} shapes (legs excluded - they belong to basePlanes)`);
                } else {
                    console.log(`   - Generated meshes for CROSS-SLICE ${index}: ${allShapes.length} shapes`);
                    crossSliceCount++;
                }
            } else if (index > 0) {
                console.log(`   - Cross-slice plane ${index + 1}: no sketch data (skipped)`);
            }
        });

        if (meshCount > 0) {
            console.log(`✅ Generated ${meshCount} total meshes (${crossSliceCount} cross-slices)`);

            // Create single compound physics body for all chair pieces (more efficient)
            this.createCompoundChairPhysicsBody();

            // Keep the rotation pivot on the chair centre
            this.updateRotationPivot();
        }

        // Restore physics state if it was running
        if (wasPhysicsPlaying) {
            this.isPhysicsPlaying = true;
            console.log('Resumed physics after mesh regeneration');
        }
    }

    /**
     * Update 3D meshes from 2D closed shapes on the selected base plane
     * @param {Array} closedShapes - Array of closed SketchPath objects
     */
    updateMeshesFromShapes(closedShapes) {
        console.log('Updating 3D meshes from', closedShapes.length, 'shapes');

        // Remove old generated meshes from scene
        this.generatedMeshes.forEach(mesh => {
            this.sceneManager.remove(mesh);
            mesh.geometry.dispose();
            mesh.material.dispose();
        });
        this.generatedMeshes = [];

        // Get the currently selected base plane
        if (this.selectedBasePlaneIndex < 0 || this.selectedBasePlaneIndex >= this.basePlanes.length) {
            console.warn('No valid base plane selected');
            return;
        }

        const basePlane = this.basePlanes[this.selectedBasePlaneIndex];
        const materialThickness = basePlane.thickness * 0.01; // Convert mm to world units (5mm = 0.05 units)

        console.log(`Generating meshes on base plane ${this.selectedBasePlaneIndex + 1} at Z=${basePlane.position.z.toFixed(3)}`);

        // Generate new meshes from closed shapes
        closedShapes.forEach((shape, index) => {
            try {
                // Scale down the 2D coordinates (they're in canvas pixels)
                const scaleFactor = 0.01; // 1 pixel = 0.01 units in 3D

                // Create a scaled copy of the path
                const scaledPath = shape.clone();
                scaledPath.points.forEach(point => {
                    point.x *= scaleFactor;
                    point.y *= scaleFactor;
                    // Also scale control points
                    if (point.controlPoint1) {
                        point.controlPoint1.x *= scaleFactor;
                        point.controlPoint1.y *= scaleFactor;
                    }
                    if (point.controlPoint2) {
                        point.controlPoint2.x *= scaleFactor;
                        point.controlPoint2.y *= scaleFactor;
                    }
                });
                // Scale hole rings too (closed strokes carry their inner
                // outline as a hole)
                (scaledPath.holes || []).forEach(hole => hole.forEach(point => {
                    point.x *= scaleFactor;
                    point.y *= scaleFactor;
                }));

                // Generate 3D mesh with extrusion
                // Extrude along Z axis (perpendicular to XY plane)
                const mesh = this.meshBuilder.createExtrudedMesh(scaledPath, {
                    depth: materialThickness, // Use actual material thickness
                    centerY: false // Don't center on Y, we'll position manually
                });

                if (mesh) {
                    // Position mesh on the base plane, CENTERED on the plane
                    // position (the extrusion itself is z-centred at +-t/2,
                    // see buildExtrudedGeometry - same convention as
                    // generateMeshesForPlane)
                    mesh.position.set(
                        basePlane.position.x,
                        basePlane.position.y,
                        basePlane.position.z
                    );

                    // No rotation needed - base planes are already in XY orientation
                    // matching the 2D canvas coordinate system

                    // Add to scene
                    mesh.userData.basePlaneIndex = planeIndex;
                    this.sceneManager.add(mesh);
                    this.generatedMeshes.push(mesh);

                    console.log(`Generated mesh ${index} on plane ${this.selectedBasePlaneIndex + 1}: Z=${basePlane.position.z.toFixed(3)}, thickness=${materialThickness.toFixed(3)}`);
                }
            } catch (error) {
                console.error('Failed to generate mesh for shape', index, error);
            }
        });

        console.log('Generated', this.generatedMeshes.length, '3D meshes');
    }

    /**
     * Generate parallel slice planes from splines
     * @param {Array} splines - Array of SketchSpline objects
     */
    generatePlanesFromSplines(splines) {
        console.log('Generating planes from', splines.length, 'splines');

        // Clear existing generated planes from scene
        this.generatedPlanes.forEach(planeViz => {
            if (planeViz.mesh) {
                this.sceneManager.remove(planeViz.mesh);
                planeViz.mesh.geometry.dispose();
                planeViz.mesh.material.dispose();
            }
            if (planeViz.wireframe) {
                this.sceneManager.remove(planeViz.wireframe);
                planeViz.wireframe.geometry.dispose();
                planeViz.wireframe.material.dispose();
            }
        });
        this.generatedPlanes = [];

        // Clear existing planes from manager
        this.slicePlaneManager.clear();

        // Clear existing cross-slice selections
        this.crossSliceSelections = [];

        // Generate planes for each spline
        splines.forEach((spline, splineIndex) => {
            // Create a CrossSliceSelection for this spline
            const crossSliceSelection = new CrossSliceSelection({
                path: spline,
                plane: null, // No parent plane in this case
                start: 0,
                end: 1,
                spacing: this.planeSpacing,
                type: CrossSliceSelection.SLICES
            });

            this.crossSliceSelections.push(crossSliceSelection);
            console.log('Created CrossSliceSelection for spline', splineIndex);
            // Generate plane data from the spline
            const planeData = spline.generateParallelPlanes(this.planeSpacing, 0, 1);

            console.log('Spline', splineIndex, 'generated', planeData.length, 'planes');

            planeData.forEach((data, planeIndex) => {
                // Scale down from canvas coordinates
                const scaleFactor = 0.01;
                const posX = data.position.x * scaleFactor;
                const posY = data.position.y * scaleFactor;

                // In 2D canvas, we draw on XY plane
                // Convert to 3D: canvas X,Y becomes world X,Y with Z=0
                // Normal in 2D becomes normal in 3D (perpendicular to path, pointing in Z direction)
                const position3D = new THREE.Vector3(posX, posY, 0);

                // For now, create planes perpendicular to XY plane, oriented by the 2D normal
                // The 2D normal tells us which direction the plane should face in the XY plane
                // We want planes that cut through the shape perpendicular to the path
                const normal3D = new THREE.Vector3(data.normal.x, data.normal.y, 0).normalize();

                // Create the SlicePlane
                const slicePlane = new SlicePlane({
                    position: position3D,
                    normal: normal3D,
                    thickness: this.getEffectiveThickness?.() ?? 5.0
                });

                // Add to manager
                this.slicePlaneManager.addPlane(slicePlane);

                // Visualize the plane
                this.visualizePlane(slicePlane, planeIndex, splineIndex);
            });
        });

        console.log('Generated', this.slicePlaneManager.getPlanes().length, 'total planes');

        // Visualize intersections
        this.visualizeIntersections();

        // Visualize slots
        this.visualizeSlots();

        // Update status display
        this.updateStatusDisplay();
    }

    /**
     * Visualize a single slice plane
     * @param {SlicePlane} slicePlane - The slice plane to visualize
     * @param {number} planeIndex - Index of plane in sequence
     * @param {number} splineIndex - Index of parent spline
     */
    visualizePlane(slicePlane, planeIndex, splineIndex) { return this.sceneVisualizers.visualizePlane(slicePlane, planeIndex, splineIndex); }

    /**
     * Visualize plane intersections
     * Creates line visualizations where planes intersect
     */
    visualizeIntersections() { return this.sceneVisualizers.visualizeIntersections(); }

    /**
     * Visualize a single plane intersection
     * @param {Object} intersection - Intersection data from SlicePlaneManager
     */
    visualizeIntersection(intersection) { return this.sceneVisualizers.visualizeIntersection(intersection); }

    /**
     * Visualize all slots as 3D geometry
     * Shows where slots will be cut into each plane
     */
    visualizeSlots() { return this.sceneVisualizers.visualizeSlots(); }

    /**
     * Visualize a single slot as 3D geometry
     * @param {SliceSlot} slot - Slot to visualize
     * @param {number} index - Slot index
     */
    visualizeSlot(slot, index) { return this.sceneVisualizers.visualizeSlot(slot, index); }

    /**
     * Visualize slice planes in the 3D scene
     * Shows semi-transparent planes and their intersection lines
     */
    visualizeSlicePlanes() { return this.sceneVisualizers.visualizeSlicePlanes(); }

    /**
     * Create Phase 1 demo scene with physics cubes
     */
    createDemoScene() {
        console.log('Creating demo scene...');

        // Create falling cubes at different positions
        const cubePositions = [
            { x: 0, y: 3, z: 0 },
            { x: -1.5, y: 5, z: 0 },
            { x: 1.5, y: 7, z: 0 }
        ];

        cubePositions.forEach((pos, index) => {
            this.createPhysicsCube(pos.x, pos.y, pos.z, {
                color: [0xff6b6b, 0x4ecdc4, 0xffe66d][index % 3],
                mass: 1.0
            });
        });

        // Create static ground
        this.createStaticGround();

        console.log(`Created ${this.cubes.length} physics cubes`);
    }

    /**
     * Create a cube with physics
     */
    createPhysicsCube(x, y, z, options = {}) {
        const {
            size = 1,
            color = 0x4CAF50,
            mass = 1.0
        } = options;

        // Create Three.js mesh
        const geometry = new THREE.BoxGeometry(size, size, size);
        const material = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.5,
            metalness: 0.3
        });
        const cube = new THREE.Mesh(geometry, material);
        cube.position.set(x, y, z);
        cube.castShadow = true;
        cube.receiveShadow = true;

        // Add to scene
        this.sceneManager.add(cube);

        // Create physics body
        this.physicsWorld.createDynamicBody(cube, {
            mass: mass,
            restitution: 0.5,
            friction: 0.7
        });

        // Apply small random rotation
        const body = this.physicsWorld.getBody(cube);
        if (body) {
            body.setAngvel({ x: Math.random() - 0.5, y: Math.random() - 0.5, z: Math.random() - 0.5 }, true);
        }

        this.cubes.push(cube);

        return cube;
    }

    /**
     * Create static ground plane (invisible, just for physics)
     */
    createStaticGround() {
        // Create invisible mesh for physics
        const geometry = new THREE.BoxGeometry(20, 0.5, 20);
        const material = new THREE.MeshStandardMaterial({
            visible: false
        });
        const ground = new THREE.Mesh(geometry, material);
        ground.position.set(0, -1.25, 0);

        // Add to scene (invisible)
        this.sceneManager.add(ground);

        // Create physics body (static)
        this.physicsWorld.createStaticBody(ground, {
            restitution: 0.3,
            friction: 0.8
        });
    }

    /**
     * Start application loop
     */
    start() {
        console.log('Starting application loop...');
        this.isRunning = true;
        this.lastTime = performance.now();
        this.animate();

        // A share deep-link (?d=<slug>) supersedes the autosave restore -
        // the cloud layer loads the shared design instead (spec D4/D6)
        if (this.cloudUI?.hasShareLink()) {
            this.cloudUI.openShareLink();
        } else {
            // Restore the last autosaved design (Java autoLoad parity)
            this.restoreAutosave();
        }
    }

    /**
     * Main animation loop
     */
    animate = () => {
        if (!this.isRunning) return;

        const currentTime = performance.now();
        const deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        // Update physics only when playing
        if (this.isPhysicsPlaying) {
            // Hand-tool spring impulse toward the cursor (before the step)
            this.updateHandGrab(deltaTime);

            this.physicsWorld.step(deltaTime);

            // Update ragdoll
            if (this.ragdoll) {
                this.ragdoll.update(deltaTime);
            }
        }

        // Sync Three.js meshes with physics bodies
        this.physicsWorld.syncMeshes();

        // Collider hull debug draw (no-op unless toggled on)
        if (this.physicsDebug?.enabled) {
            this.physicsDebug.update();
        }

        // Pose the sketch overlay frame (and pointer raycasting) with the
        // chair's physics delta so drawing/editing follows the simulation
        this.updatePhysicsFrameDelta();

        // Keep unselected layers dimmed (cheap: writes only on change)
        this.updateLayerMeshOpacity();

        // Render control points in 3D when select tool is active
        this.renderControlPoints();

        // Cross-chair curve panel: cheap memoized selection check
        this.uiManager?.panels?.strokeModifier?.updateCheap?.();

        // Measurement label follows the camera/scene (no-op when unused)
        this.updateMeasureLabel();

        // Figure control pill tracks the head as the camera/figure moves
        // (cheap no-op unless the figure is selected)
        if (this._figureSelected) this.updateFigureWidget();

        // Render scene
        this.sceneManager.render();

        // Update FPS counter
        this.updateFPS(currentTime);

        // Continue loop
        requestAnimationFrame(this.animate);
    }

    /**
     * Update FPS counter in UI
     */
    updateFPS(currentTime) {
        this.frameCount++;

        if (currentTime - this.fpsUpdateTime > 1000) {
            this.fps = Math.round(this.frameCount * 1000 / (currentTime - this.fpsUpdateTime));
            this.frameCount = 0;
            this.fpsUpdateTime = currentTime;

            // Update 3D UI
            const fpsElement = document.getElementById('fps');
            if (fpsElement) {
                fpsElement.textContent = this.fps;
            }

            // Update sketch UI
            const pathCountElement = document.getElementById('path-count');
            if (pathCountElement) {
                pathCountElement.textContent = this.sketchPlane.closedShapes.length;
            }
        }
    }

    /**
     * Update status display with plane/intersection/slot info
     */
    updateStatusDisplay() {
        // Update current base plane
        const currentPlaneElement = document.getElementById('current-plane');
        if (currentPlaneElement) {
            if (this.selectedBasePlaneIndices.size === this.basePlanes.length) {
                currentPlaneElement.textContent = 'All';
            } else if (this.selectedBasePlaneIndices.size === 1) {
                const selected = Array.from(this.selectedBasePlaneIndices)[0];
                currentPlaneElement.textContent = selected + 1;
            } else {
                currentPlaneElement.textContent = `${this.selectedBasePlaneIndices.size}`;
            }
        }

        // Update total base planes
        const totalPlanesElement = document.getElementById('total-planes');
        if (totalPlanesElement) {
            totalPlanesElement.textContent = this.basePlanes.length;
        }

        // Update plane count
        const planeCountElement = document.getElementById('plane-count');
        if (planeCountElement) {
            planeCountElement.textContent = this.slicePlaneManager.getPlanes().length;
        }

        // Update intersection count
        const intersectionCountElement = document.getElementById('intersection-count');
        if (intersectionCountElement) {
            intersectionCountElement.textContent = this.slicePlaneManager.intersections.size;
        }

        // Update slot count
        const slotCountElement = document.getElementById('slot-count');
        if (slotCountElement) {
            slotCountElement.textContent = this.slicePlaneManager.getSlots().length;
        }

        // Update spacing value
        const spacingValueElement = document.getElementById('spacing-value');
        if (spacingValueElement) {
            spacingValueElement.textContent = this.planeSpacing;
        }

        // Keep the Layers panel (incl. the chair-curve slider) in sync -
        // undo/redo and design loads change the store out from under it
        this.uiManager?.panels?.layers?.update?.();
    }

    /**
     * Create single compound physics body for all chair meshes
     * More efficient than individual bodies - reduces physics overhead
     */
    createCompoundChairPhysicsBody() { return this.chairPhysics.createCompoundChairPhysicsBody(); }

    /**
     * Clear all chair physics bodies
     */
    clearChairPhysicsBodies() { return this.chairPhysics.clearChairPhysicsBodies(); }

    /**
     * Test chair stability with ragdoll
     * @param {number} duration - Test duration in seconds
     * @returns {Promise<Object>} Stability test results
     */
    async testChairStability(duration = 3.0) {
        if (!this.ragdoll) {
            console.warn('No ragdoll available for stability testing');
            return { stable: false, error: 'No ragdoll' };
        }

        if (this.generatedMeshes.length === 0) {
            console.warn('No chair meshes available for stability testing');
            return { stable: false, error: 'No chair' };
        }

        // Make sure ragdoll is visible and positioned (pre-posed seated)
        this.ragdoll.setVisible(true);
        this.ragdoll.positionOnChair(this.generatedMeshes, this.physicsWorld?.groundY ?? -5);
        this.chairPhysics.frameFigureAndChair();

        // Start physics simulation
        const wasPlaying = this.isPhysicsPlaying;
        this.isPhysicsPlaying = true;

        console.log(`Testing chair stability for ${duration} seconds...`);

        // Run stability test
        const results = await this.ragdoll.testStability(duration);

        // Restore physics state
        this.isPhysicsPlaying = wasPlaying;

        console.log('Stability test results:', results);
        return results;
    }

    /**
     * Toggle physics simulation on/off
     * @param {boolean} play - True to play, false to pause
     */
    togglePhysics(play) { return this.chairPhysics.togglePhysics(play); }

    /**
     * Save current mesh positions/rotations before physics simulation
     */
    savePhysicsPositions() { return this.chairPhysics.savePhysicsPositions(); }

    /**
     * Restore mesh positions/rotations to pre-physics state
     */
    restorePhysicsPositions() { return this.chairPhysics.restorePhysicsPositions(); }

    /**
     * Reset physics simulation
     */
    resetPhysics() { return this.chairPhysics.resetPhysics(); }

    /**
     * Toggle ragdoll visibility
     * @returns {boolean} New visibility state
     */
    toggleRagdoll() { return this.chairPhysics.toggleRagdoll(); }

    /**
     * Scale ragdoll size
     * @param {number} factor - Scale factor (e.g., 1.1 for 10% bigger)
     */
    scaleRagdoll(factor) { return this.chairPhysics.scaleRagdoll(factor); }

    /**
     * Toggle the ergonomic figure between standing and seated (in place)
     * @returns {string} the new pose name ('standing' | 'seated')
     */
    toggleFigurePose() { return this.chairPhysics.toggleFigurePose(); }

    /**
     * Stop application
     */
    stop() {
        console.log('Stopping application...');
        this.isRunning = false;
    }

    /**
     * Cleanup resources
     */
    dispose() {
        this.stop();
        this.sceneManager.dispose();
        this.physicsWorld.dispose();
    }
}
